/**
 * Admin "Deals & Proposals" workspace (v2-1-3) — route /admin/deals.
 *
 * Two surfaces, staff-gated client-side (RequireStaff) + server-side (staff JWT):
 *   1. Creative Library — every GCS asset grouped by client (folder-style), tagged
 *      v1/v2 + format, with INLINE HTML5 video players + PDF open/preview. The
 *      owner can view & play everything per client and tick assets to attach.
 *   2. Proposals — list + "New proposal" composer (client, campaign, intro, attach
 *      ticked library assets + extra links, status) and the generated PRIVATE share
 *      link with a copy button.
 *
 * Admin calls go through authedFetch (Bearer JWT; 401 -> /admin/login). NO public
 * gallery exists — clients only ever see a proposal via its private /p/<token> link.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { AudioPlayer, ImagePreview, PdfPreview, VideoPlayer } from '../components/MediaPlayer';
import { ApiError } from '../lib/api';
import { isSafeHttpUrl } from '../lib/safeUrl';
import {
  addProposalItem,
  createProposal,
  deleteCreativeAsset,
  downloadCreativeAsset,
  deleteProposal,
  generatePack,
  generatePackFromManifest,
  manifestMissing,
  getProposal,
  removeProposalItem,
  generatePitchImage,
  generatePitchPdf,
  generatePitchVideo,
  generatePitchVoiceover,
  getLineage,
  getRenderTier,
  getVideoJob,
  listCreativeAssets,
  setRenderTier,
  setVideoPoster,
  listProposals,
  proposalShareUrl,
  regenerateAsset,
  setCurrentVersion,
  updateProposal,
  type AssetLineage,
  type AssetVersion,
  type CreativeAsset,
  type GeneratePack,
  type GeneratePackResponse,
  type PackInclude,
  type ClientManifest,
  type ManifestBrand,
  type ManifestFormat,
  type ManifestIndustry,
  type ManifestMissingEntry,
  type GeneratePackFromManifestResponse,
  MANIFEST_FORMATS,
  type RegeneratedAsset,
  type GeneratePitchImage,
  type GeneratePitchPdf,
  type GeneratePitchVideo,
  type GeneratePitchVoiceover,
  type ImageAspect,
  type MediaType,
  type NewProposalItem,
  type Proposal,
  type ProposalItem,
  type ProposalListItem,
  type ProposalStatus,
  type VideoRenderJob,
  type VideoType,
  type Voice,
} from '../lib/adminApi';

// --- corrections & versioning loop (v3-5) ------------------------------------

/**
 * When a generator form is opened in "edit & regenerate" mode it gets this
 * context: the source asset id (to regenerate against), the spec to prefill the
 * fields from, and an onDone callback to close the editor.
 */
interface RegenContext {
  sourceId: string;
  spec: Record<string, unknown>;
  onDone?: () => void;
}

/** Banner shown atop a generator form when it's in edit-&-regenerate mode. */
function RegenNote() {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 p-3" role="note">
      <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M4 4v6h6M20 20v-6h-6" />
        <path d="M20 9A8 8 0 0 0 5.6 5.6L4 10M4 15l1.6 4.4A8 8 0 0 0 20 15" />
      </svg>
      <p className="text-xs font-medium text-primary-700">
        Editing the inputs and submitting <strong>creates a new version</strong> of this asset — it becomes the current version, and the previous one stays in the version history (you can revert).
      </p>
    </div>
  );
}

// --- small shared bits -------------------------------------------------------

const STATUS_STYLES: Record<ProposalStatus, string> = {
  draft: 'bg-surface-muted text-ink-700',
  sent: 'bg-primary-50 text-primary-700',
  won: 'bg-emerald-50 text-emerald-700',
  lost: 'bg-rose-50 text-rose-700',
};

const STATUSES: ProposalStatus[] = ['draft', 'sent', 'won', 'lost'];

function Tag({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'accent' | 'warm' | 'design';
}) {
  const styles =
    tone === 'accent'
      ? 'bg-accent-500/10 text-accent-600'
      : tone === 'warm'
        ? 'bg-warm-500/15 text-warm-600'
        : tone === 'design'
          ? 'bg-primary-500/10 text-primary-700 ring-1 ring-primary-500/25'
          : 'bg-surface-muted text-ink-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${styles}`}>
      {children}
    </span>
  );
}

function Panel({ title, subtitle, action, children }: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-bold text-ink">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-ink-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// --- creative library --------------------------------------------------------

/** A logical asset (one lineage): its CURRENT version + how many versions exist. */
interface LineageEntry {
  lineage_id: string;
  current: CreativeAsset;
  versionCount: number;
}

interface AssetGroup {
  client_slug: string;
  client_name: string;
  lineages: LineageEntry[];
}

/**
 * Parse an asset's `created_at` into an epoch-ms number, or `null` when it's
 * missing/unparseable. Shared by the lineage sort and the card's freshness line
 * so "what counts as a valid timestamp" is decided in exactly one place.
 */
function parseCreatedAt(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

/**
 * Group assets by client, then by lineage — showing the CURRENT version per
 * logical asset and how many versions it has. Non-current versions are tucked
 * away behind the per-card "history" affordance (v3-5).
 */
function groupByClient(assets: CreativeAsset[]): AssetGroup[] {
  // First collapse to lineages: pick the current version (fallback: highest
  // version) + count how many versions each lineage has.
  const lineages = new Map<string, CreativeAsset[]>();
  for (const a of assets) {
    const key = a.lineage_id ?? a.id;
    const arr = lineages.get(key);
    if (arr) arr.push(a);
    else lineages.set(key, [a]);
  }

  const map = new Map<string, AssetGroup>();
  for (const [lineageId, versions] of lineages) {
    const current =
      versions.find((v) => v.is_current) ??
      versions.slice().sort((x, y) => (y.version ?? 1) - (x.version ?? 1))[0];
    let g = map.get(current.client_slug);
    if (!g) {
      g = { client_slug: current.client_slug, client_name: current.client_name, lineages: [] };
      map.set(current.client_slug, g);
    }
    g.lineages.push({ lineage_id: lineageId, current, versionCount: versions.length });
  }
  for (const g of map.values()) {
    // Newest first by the current version's created_at, so the freshest asset
    // surfaces at the top of each client's folder. A missing/unparseable
    // timestamp is a data anomaly, so sort it to the TOP (treated as +Infinity)
    // where the operator will notice it, rather than burying it at the bottom
    // where it's indistinguishable from a legitimately old asset. Fall back to
    // title as a stable tiebreaker on equal timestamps to avoid jitter.
    g.lineages.sort((x, y) => {
      const vx = parseCreatedAt(x.current.created_at) ?? Infinity;
      const vy = parseCreatedAt(y.current.created_at) ?? Infinity;
      if (vy !== vx) return vy - vx;
      return x.current.title.localeCompare(y.current.title);
    });
  }
  return Array.from(map.values()).sort((x, y) => x.client_name.localeCompare(y.client_name));
}

/** Renders the right inline player/preview for an asset's media type. */
function MediaPreview({ asset }: { asset: CreativeAsset }) {
  if (asset.media_type === 'video')
    return (
      <VideoPlayer
        src={asset.gcs_url}
        label={asset.title}
        orientation={asset.orientation}
        poster={asset.poster_url}
      />
    );
  if (asset.media_type === 'image')
    return <ImagePreview src={asset.gcs_url} label={asset.title} />;
  if (asset.media_type === 'audio')
    return <AudioPlayer src={asset.gcs_url} label={asset.title} />;
  return <PdfPreview src={asset.gcs_url} label={asset.title} />;
}

// --- video poster / thumbnail override (v5-1-6) --------------------------------

/**
 * Inline thumbnail-override control for a single video creative asset.
 *
 * The operator can:
 *   (a) Scrub the <video> element to the frame they want, then click
 *       "Use this frame" to capture the current playback position, OR
 *   (b) Type a timestamp (seconds) into the number input directly.
 *
 * A live preview is shown by seeking the video to the chosen timestamp.
 * "Set as thumbnail" calls setVideoPoster and updates the card on success.
 *
 * Accessibility notes (ui-ux-pro-max):
 *   - All interactive elements have min-h-[44px] / min-w-[44px] touch targets.
 *   - Inputs have explicit <label> associations.
 *   - Loading state disables + shows a spinner (loading-button rule).
 *   - Errors render in role="alert" near the control.
 */
function VideoPosterControl({
  asset,
  onUpdated,
}: {
  asset: CreativeAsset;
  /** Called with the updated asset after a successful setVideoPoster call. */
  onUpdated: (updated: CreativeAsset) => void;
}) {
  // Whether the operator has expanded the thumbnail panel.
  const [open, setOpen] = useState(false);

  // The video element ref — used to seek the player and read currentTime.
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // The timestamp the operator has chosen (seconds). `null` means "unset" — the
  // input is empty or holds a non-finite value (parseFloat("") === NaN), in which
  // case we never send anything to the API and the submit stays disabled.
  const [ts, setTs] = useState<number | null>(0);

  // Pending / success / error state.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Holds the success-flash auto-close timer so it can be cleared on unmount
  // (otherwise it can fire setState on an unmounted component).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clamp a raw number to a safe timestamp (non-negative, finite). Returns null
  // for non-finite input (NaN/Infinity) so callers can treat it as "unset".
  function clamp(v: number): number | null {
    if (!Number.isFinite(v)) return null;
    return v < 0 ? 0 : v;
  }

  // Reset transient feedback whenever the panel (re)opens, so stale ok/error/busy
  // from a previous interaction don't linger.
  useEffect(() => {
    if (open) {
      setOk(false);
      setError(null);
      setBusy(false);
    }
  }, [open]);

  // Clear the pending success-flash timer on unmount.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Seek the video to the chosen timestamp for a live preview.
  function seekPreview(seconds: number) {
    const el = videoRef.current;
    if (!el) return;
    // Kick the video out of "not loaded" state so seeking works.
    if (el.readyState === 0) {
      el.load();
    }
    el.currentTime = seconds;
  }

  function handleTsChange(raw: string) {
    // parseFloat("") === NaN and a partial value can misbehave — clamp() returns
    // null for those, which we treat as "unset" (don't seek, don't send to the
    // API, disable submit). Negatives clamp to 0.
    const parsed = clamp(parseFloat(raw));
    setTs(parsed);
    setOk(false);
    setError(null);
    if (parsed !== null) seekPreview(parsed);
  }

  function captureCurrentTime() {
    const el = videoRef.current;
    const current = clamp(el?.currentTime ?? 0) ?? 0;
    setTs(current);
    setOk(false);
    setError(null);
  }

  async function applyPoster() {
    // Guard: never POST a NaN/unset timestamp.
    if (ts === null) return;
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      const updated = await setVideoPoster(asset.id, ts);
      setOk(true);
      onUpdated(updated);
      // Auto-close after a short success flash so the card doesn't stay expanded.
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        setOpen(false);
        setOk(false);
        closeTimerRef.current = null;
      }, 1800);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set the thumbnail.');
    } finally {
      setBusy(false);
    }
  }

  const hasPoster = Boolean(asset.poster_url);
  // Only put poster_url into an <img src> when it's a safe http(s) URL
  // (XSS/SSRF guard — poster_url is pipeline/operator-derived). Anything else
  // renders no preview rather than an unguarded src.
  const safePosterUrl =
    asset.poster_url && isSafeHttpUrl(asset.poster_url) ? asset.poster_url : null;

  return (
    <div className="border-t border-line pt-2">
      {/* Toggle row */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {/* Film-frame icon */}
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
          <path d="M7 2v20M17 2v20M2 12h20M2 7h5M17 7h5M2 17h5M17 17h5" />
        </svg>
        {hasPoster ? 'Change thumbnail' : 'Choose thumbnail'}
        <svg
          viewBox="0 0 24 24"
          className={`h-3 w-3 text-ink-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Expanded thumbnail picker */}
      {open && (
        <div className="mt-2 space-y-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
          <p className="text-xs text-ink-500">
            Scrub the video below to the frame you want, then click{' '}
            <strong>Use this frame</strong> — or type the exact timestamp.
          </p>

          {/* The video player used purely for scrubbing / preview. */}
          <div className="overflow-hidden rounded-lg border border-line bg-black">
            <video
              ref={videoRef}
              src={asset.gcs_url}
              preload="metadata"
              playsInline
              controls
              onTimeUpdate={() => {
                const el = videoRef.current;
                if (el) setTs(clamp(el.currentTime) ?? 0);
              }}
              className="aspect-video h-auto w-full bg-black"
              aria-label={`Preview ${asset.title} — scrub to choose thumbnail frame`}
            />
          </div>

          {/* Timestamp controls */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[120px]">
              <label
                htmlFor={`poster-ts-${asset.id}`}
                className="block text-xs font-medium text-ink-700"
              >
                Timestamp (seconds)
              </label>
              <input
                id={`poster-ts-${asset.id}`}
                type="number"
                min="0"
                step="0.1"
                value={ts ?? ''}
                onChange={(e) => handleTsChange(e.target.value)}
                className="mt-1 w-full min-h-[44px] rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <button
              type="button"
              onClick={captureCurrentTime}
              className="min-h-[44px] cursor-pointer rounded-lg border border-line bg-white px-3 py-2 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Use this frame
            </button>
          </div>

          {/* Current poster preview — only when poster_url is a safe http(s) URL
              (5a: never put an unguarded poster_url into an <img src>). */}
          {safePosterUrl && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-ink-500">Current thumbnail:</p>
              <img
                src={safePosterUrl}
                alt="Current thumbnail"
                className="h-auto max-h-24 rounded-lg border border-line object-cover"
              />
            </div>
          )}

          {/* Error / success feedback */}
          {error && (
            <p role="alert" className="text-xs text-rose-600">
              {error}
            </p>
          )}
          {ok && (
            <p role="status" className="text-xs font-medium text-emerald-600">
              Thumbnail updated.
            </p>
          )}

          {/* Apply button */}
          <button
            type="button"
            disabled={busy || ts === null}
            onClick={applyPoster}
            className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl bg-aurora px-4 py-2 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
              </svg>
            )}
            {busy ? 'Setting thumbnail…' : 'Set as thumbnail'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Can this asset be edited & regenerated? (Generated kinds with a spec only.) */
function isRegenerable(asset: CreativeAsset): boolean {
  return Boolean(
    asset.generation_kind && asset.generation_kind !== 'manual',
  );
}

function AssetCard({
  entry,
  selected,
  onToggle,
  onEditRegenerate,
  onShowHistory,
  onDelete,
  onPosterChange,
}: {
  entry: LineageEntry;
  selected: boolean;
  onToggle: (a: CreativeAsset) => void;
  onEditRegenerate: (a: CreativeAsset) => void;
  onShowHistory: (lineageId: string) => void;
  onDelete: (a: CreativeAsset) => void;
  /** Called with the server-returned asset after a poster override succeeds. */
  onPosterChange: (updated: CreativeAsset) => void;
}) {
  const asset = entry.current;
  const regenerable = isRegenerable(asset);
  const hasHistory = entry.versionCount > 1 || (asset.version ?? 1) > 1;
  return (
    <div
      className={[
        'flex flex-col gap-3 rounded-xl border bg-surface-alt/40 p-3 transition-colors duration-200',
        selected ? 'border-primary ring-2 ring-primary/30' : 'border-line',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{asset.title}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {/* Lineage revision (how many times THIS asset was regenerated). */}
            <Tag tone="warm">v{asset.version ?? 1}</Tag>
            {/* Design iteration (v1/v2 design system) — distinct from the lineage
                revision so the BMW-colour TV ad (design_version "v2") is clearly
                marked as v2 rather than blending in with seeded v1 assets. */}
            <Tag tone="design">Design {asset.design_version}</Tag>
            <Tag tone="accent">{asset.format_label}</Tag>
            {asset.orientation && <Tag>{asset.orientation}</Tag>}
          </div>
          {/* Freshness indicator so the operator can spot the latest asset without
              opening the History drawer (hotfix-1-1). Subtle text, not a badge, so
              it doesn't compete with the version/format tags above. Only render
              when created_at actually parses — a truthy-but-malformed timestamp
              must not surface a literal "Invalid Date" to the operator. */}
          {(() => {
            const t = parseCreatedAt(asset.created_at);
            if (t === null) return null;
            return (
              <p className="mt-1 flex items-center gap-1 text-xs text-ink-500">
                <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                Updated{' '}
                <time dateTime={asset.created_at} title={new Date(t).toLocaleString()}>
                  {new Date(t).toLocaleDateString()}
                </time>
              </p>
            );
          })()}
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-ink-700">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(asset)}
            className="h-4 w-4 cursor-pointer rounded border-line text-primary focus:ring-primary"
            aria-label={`Attach ${asset.title} to a proposal`}
          />
          Attach
        </label>
      </div>
      <MediaPreview asset={asset} />
      {/* v3-5 — edit & regenerate + version history affordances. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Force a real download (save dialog) via the same-origin staff proxy — a
            plain cross-origin GCS link just opens the file in a tab. Falls back to
            opening the URL if the proxy download fails. */}
        <button
          type="button"
          onClick={() =>
            downloadCreativeAsset(asset.id, asset.gcs_url.split('/').pop() || asset.title).catch(
              () => window.open(asset.gcs_url, '_blank', 'noopener'),
            )
          }
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={`Download ${asset.title}`}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5" />
            <path d="M12 15V3" />
          </svg>
          Download
        </button>
        {regenerable && (
          <button
            type="button"
            onClick={() => onEditRegenerate(asset)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-2.5 py-1 text-xs font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
            Edit &amp; regenerate
          </button>
        )}
        {hasHistory && (
          <button
            type="button"
            onClick={() => onShowHistory(entry.lineage_id)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 3v5h5" />
              <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
              <path d="M12 7v5l4 2" />
            </svg>
            History ({entry.versionCount})
          </button>
        )}
        {/* Delete this asset from the library (row + GCS object). */}
        <button
          type="button"
          onClick={() => onDelete(asset)}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 transition-colors duration-200 hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          aria-label={`Delete ${asset.title}`}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M10 11v6M14 11v6" />
          </svg>
          Delete
        </button>
      </div>
      {/* v5-1-6 — thumbnail / poster override (video assets only). */}
      {asset.media_type === 'video' && (
        <VideoPosterControl asset={asset} onUpdated={onPosterChange} />
      )}
    </div>
  );
}

/**
 * localStorage key for the open-folder set (nice-to-have persistence). Stores an
 * array of client_slugs so the owner's chosen folder survives a page reload.
 */
const LIBRARY_OPEN_KEY = 'mktg.creativeLibrary.openClients';

function loadOpenClients(): Set<string> {
  try {
    const raw = localStorage.getItem(LIBRARY_OPEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function persistOpenClients(open: Set<string>): void {
  try {
    localStorage.setItem(LIBRARY_OPEN_KEY, JSON.stringify([...open]));
  } catch {
    /* localStorage unavailable (private mode / SSR) — persistence is best-effort. */
  }
}

/**
 * Creative Library — assets grouped into per-client accordion folders.
 *
 * Default: ALL folders collapsed. Clicking a folder header opens it as a
 * single-open accordion (opening one closes the others) so the owner can focus
 * on the one client they're working with. "Expand all" overrides this to open
 * every folder; "Collapse all" closes everything. Open state is persisted to
 * localStorage so it survives reloads.
 */
function CreativeLibrary({
  assets,
  selected,
  onToggle,
  onEditRegenerate,
  onShowHistory,
  onDelete,
  onPosterChange,
}: {
  assets: CreativeAsset[];
  selected: Map<string, CreativeAsset>;
  onToggle: (a: CreativeAsset) => void;
  onEditRegenerate: (a: CreativeAsset) => void;
  onShowHistory: (lineageId: string) => void;
  onDelete: (a: CreativeAsset) => void;
  onPosterChange: (updated: CreativeAsset) => void;
}) {
  // Default: all folders collapsed. We hydrate any previously-open folders from
  // localStorage (nice-to-have), but the baseline is an empty set.
  const [openClients, setOpenClients] = useState<Set<string>>(() => loadOpenClients());
  const groups = useMemo(() => groupByClient(assets), [assets]);

  useEffect(() => {
    persistOpenClients(openClients);
  }, [openClients]);

  // Single-open accordion: clicking a closed folder opens just that one (closing
  // others); clicking the open folder collapses it.
  function toggleClient(slug: string) {
    setOpenClients((prev) => (prev.has(slug) && prev.size === 1 ? new Set() : new Set([slug])));
  }

  function expandAll() {
    setOpenClients(new Set(groups.map((g) => g.client_slug)));
  }

  function collapseAll() {
    setOpenClients(new Set());
  }

  if (groups.length === 0) {
    return <p className="text-sm text-ink-500">No creative assets in the library yet.</p>;
  }

  const anyOpen = openClients.size > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={expandAll}
          disabled={openClients.size === groups.length}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          disabled={!anyOpen}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Collapse all
        </button>
      </div>
      {groups.map((g) => {
        const open = openClients.has(g.client_slug);
        const panelId = `library-folder-${g.client_slug}`;
        const headerId = `library-folder-header-${g.client_slug}`;
        return (
          <div key={g.client_slug} className="overflow-hidden rounded-xl border border-line">
            <button
              type="button"
              id={headerId}
              onClick={() => toggleClient(g.client_slug)}
              aria-expanded={open}
              aria-controls={panelId}
              className="flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-2 bg-surface-muted px-4 py-3 text-left transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
            >
              <span className="flex items-center gap-2">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
                <span className="font-heading text-sm font-bold text-ink">{g.client_name}</span>
                <Tag>{g.lineages.length} assets</Tag>
              </span>
              <svg
                viewBox="0 0 24 24"
                className={`h-4 w-4 text-ink-500 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {open && (
              <div
                id={panelId}
                role="region"
                aria-labelledby={headerId}
                className="grid gap-3 p-3 sm:grid-cols-2 xl:grid-cols-3"
              >
                {g.lineages.map((entry) => (
                  <AssetCard
                    key={entry.lineage_id}
                    entry={entry}
                    selected={selected.has(entry.current.id)}
                    onToggle={onToggle}
                    onEditRegenerate={onEditRegenerate}
                    onShowHistory={onShowHistory}
                    onDelete={onDelete}
                    onPosterChange={onPosterChange}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export { CreativeLibrary, groupByClient };

// --- new proposal composer ---------------------------------------------------

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  );
}

function ExtraLinkRow({
  onAdd,
}: {
  onAdd: (item: NewProposalItem) => void;
}) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [media, setMedia] = useState<'video' | 'pdf'>('video');
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto_auto]">
      <input
        aria-label="Extra link label"
        placeholder="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <input
        aria-label="Extra link URL"
        placeholder="https://…"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <select
        aria-label="Extra link media type"
        value={media}
        onChange={(e) => setMedia(e.target.value as 'video' | 'pdf')}
        className="cursor-pointer rounded-lg border border-line px-2 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        <option value="video">Video</option>
        <option value="pdf">PDF</option>
      </select>
      <button
        type="button"
        disabled={!label.trim() || !url.trim()}
        onClick={() => {
          onAdd({ label: label.trim(), url: url.trim(), media_type: media });
          setLabel('');
          setUrl('');
        }}
        className="cursor-pointer rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add link
      </button>
    </div>
  );
}

function NewProposalForm({
  attachedFromLibrary,
  onRemoveLibraryItem,
  onCreated,
}: {
  attachedFromLibrary: CreativeAsset[];
  onRemoveLibraryItem: (id: string) => void;
  onCreated: (token: string) => void;
}) {
  const [clientName, setClientName] = useState('');
  const [campaignTitle, setCampaignTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [statusVal, setStatusVal] = useState<ProposalStatus>('draft');
  const [extraLinks, setExtraLinks] = useState<NewProposalItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items: NewProposalItem[] = [
    ...attachedFromLibrary.map((a, i) => ({
      label: a.title,
      url: a.gcs_url,
      media_type: a.media_type,
      sort_order: i,
    })),
    ...extraLinks.map((l, i) => ({ ...l, sort_order: attachedFromLibrary.length + i })),
  ];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await createProposal({
        client_name: clientName.trim(),
        campaign_title: campaignTitle.trim(),
        intro_md: intro.trim() || undefined,
        status: statusVal,
        items,
      });
      setClientName('');
      setCampaignTitle('');
      setIntro('');
      setStatusVal('draft');
      setExtraLinks([]);
      onCreated(created.public_token);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not create the proposal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cp-client" className="block text-sm font-medium text-ink-700">
            Client name
          </label>
          <input
            id="cp-client"
            required
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="cp-campaign" className="block text-sm font-medium text-ink-700">
            Campaign title
          </label>
          <input
            id="cp-campaign"
            required
            value={campaignTitle}
            onChange={(e) => setCampaignTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      <div>
        <label htmlFor="cp-intro" className="block text-sm font-medium text-ink-700">
          Intro message <span className="font-normal text-ink-500">(optional)</span>
        </label>
        <textarea
          id="cp-intro"
          rows={3}
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          placeholder="A short note your client sees at the top of the proposal…"
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div>
        <label htmlFor="cp-status" className="block text-sm font-medium text-ink-700">
          Status
        </label>
        <select
          id="cp-status"
          value={statusVal}
          onChange={(e) => setStatusVal(e.target.value as ProposalStatus)}
          className="mt-1 cursor-pointer rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <p className="text-sm font-medium text-ink-700">
          Attached from library ({attachedFromLibrary.length})
        </p>
        {attachedFromLibrary.length === 0 ? (
          <p className="mt-1 text-xs text-ink-500">
            Tick “Attach” on assets in the Creative Library to include them.
          </p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {attachedFromLibrary.map((a) => (
              <li key={a.id}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                  {a.title}
                  <button
                    type="button"
                    onClick={() => onRemoveLibraryItem(a.id)}
                    aria-label={`Remove ${a.title}`}
                    className="cursor-pointer rounded-full text-primary-700/70 hover:text-primary-700"
                  >
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-ink-700">Extra links ({extraLinks.length})</p>
        <div className="mt-2 space-y-2">
          {extraLinks.map((l, i) => (
            <div key={`${l.url}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-surface-muted px-3 py-1.5 text-xs">
              <span className="min-w-0 truncate">
                <span className="font-semibold text-ink">{l.label}</span>{' '}
                <span className="text-ink-500">({l.media_type})</span>
              </span>
              <button
                type="button"
                onClick={() => setExtraLinks((cur) => cur.filter((_, j) => j !== i))}
                aria-label={`Remove ${l.label}`}
                className="cursor-pointer text-ink-500 hover:text-rose-600"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          <ExtraLinkRow onAdd={(item) => setExtraLinks((cur) => [...cur, item])} />
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || items.length === 0}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-aurora px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Creating…' : 'Create proposal & get share link'}
      </button>
      {items.length === 0 && (
        <p className="text-xs text-ink-500">Attach at least one asset or link to create a proposal.</p>
      )}
    </form>
  );
}

// --- proposals list ----------------------------------------------------------

/** A small media-type pill for a proposal item (matches the library Tag tones). */
function ItemMediaTag({ mediaType }: { mediaType: MediaType }) {
  const tone = mediaType === 'video' ? 'accent' : mediaType === 'pdf' ? 'warm' : 'muted';
  return <Tag tone={tone}>{mediaType}</Tag>;
}

/**
 * The "Add item" affordance on an expanded proposal. Two ways to add an item:
 *   (a) Attach an asset from THIS client's Creative Library (reuses the same
 *       gcs_url + media_type the New-proposal composer attaches), or
 *   (b) Paste a custom label + absolute http(s) URL (validated client-side; the
 *       backend enforces it too).
 * On success it calls addProposalItem and the parent refreshes the items list.
 */
function AddItemForm({
  proposal,
  clientAssets,
  existingUrls,
  onAdded,
}: {
  proposal: Proposal;
  clientAssets: CreativeAsset[];
  existingUrls: Set<string>;
  onAdded: (updated: Proposal) => void;
}) {
  const [mode, setMode] = useState<'library' | 'custom'>(
    clientAssets.length > 0 ? 'library' : 'custom',
  );
  // Library mode.
  const [assetId, setAssetId] = useState('');
  // Custom mode.
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [media, setMedia] = useState<MediaType>('video');
  const [touched, setTouched] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const urlValid = isSafeHttpUrl(url.trim());

  async function add(item: NewProposalItem) {
    setBusy(true);
    setError(null);
    try {
      const updated = await addProposalItem(proposal.id, item);
      onAdded(updated);
      // Reset the inputs for the next add.
      setAssetId('');
      setLabel('');
      setUrl('');
      setTouched(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not attach the item.');
    } finally {
      setBusy(false);
    }
  }

  function submitLibrary() {
    const asset = clientAssets.find((a) => a.id === assetId);
    if (!asset) return;
    void add({
      label: asset.title,
      url: asset.gcs_url,
      media_type: asset.media_type,
      sort_order: proposal.items.length,
    });
  }

  function submitCustom() {
    setTouched(true);
    if (!label.trim() || !urlValid) return;
    void add({
      label: label.trim(),
      url: url.trim(),
      media_type: media,
      sort_order: proposal.items.length,
    });
  }

  const tabBtn = (active: boolean) =>
    [
      'cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      active ? 'bg-white text-primary-700 shadow-card' : 'text-ink-700 hover:bg-white/60',
    ].join(' ');

  return (
    <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-ink-700">Add an item</p>
        <div className="inline-flex rounded-lg bg-surface-muted p-0.5" role="tablist" aria-label="Add item source">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'library'}
            onClick={() => setMode('library')}
            className={tabBtn(mode === 'library')}
          >
            From library
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'custom'}
            onClick={() => setMode('custom')}
            className={tabBtn(mode === 'custom')}
          >
            Custom link
          </button>
        </div>
      </div>

      {mode === 'library' ? (
        clientAssets.length === 0 ? (
          <p className="mt-2 text-xs text-ink-500">
            No library assets for {proposal.client_name}. Use “Custom link” to paste one.
          </p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
            <select
              aria-label="Library asset to attach"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="min-h-[44px] cursor-pointer rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Choose an asset…</option>
              {clientAssets.map((a) => (
                <option key={a.id} value={a.id} disabled={existingUrls.has(a.gcs_url)}>
                  {a.title} ({a.media_type}){existingUrls.has(a.gcs_url) ? ' — already added' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || !assetId}
              onClick={submitLibrary}
              className="min-h-[44px] cursor-pointer rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        )
      ) : (
        <div className="mt-2 space-y-2">
          <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto_auto]">
            <input
              aria-label="New item label"
              placeholder="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="min-h-[44px] rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <input
              aria-label="New item URL"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => setTouched(true)}
              className="min-h-[44px] rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <select
              aria-label="New item media type"
              value={media}
              onChange={(e) => setMedia(e.target.value as MediaType)}
              className="min-h-[44px] cursor-pointer rounded-lg border border-line px-2 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="video">Video</option>
              <option value="pdf">PDF</option>
              <option value="image">Image</option>
              <option value="audio">Audio</option>
            </select>
            <button
              type="button"
              disabled={busy || !label.trim() || !urlValid}
              onClick={submitCustom}
              className="min-h-[44px] cursor-pointer rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
          {touched && url.trim() && !urlValid && (
            <p role="alert" className="text-xs text-rose-600">
              Enter an absolute http(s) URL (e.g. https://…).
            </p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** One item row in an expanded proposal: label + media tag + url + Remove. */
function ProposalItemRow({
  item,
  busy,
  onRemove,
}: {
  item: ProposalItem;
  busy: boolean;
  onRemove: (item: ProposalItem) => void;
}) {
  return (
    <li className="flex items-start justify-between gap-2 rounded-lg bg-surface-alt/40 px-3 py-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-ink">{item.label}</span>
          <ItemMediaTag mediaType={item.media_type} />
        </div>
        {isSafeHttpUrl(item.url) ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-0.5 block max-w-full truncate text-xs text-primary-700 hover:underline"
            title={item.url}
          >
            {item.url}
          </a>
        ) : (
          <p className="mt-0.5 truncate text-xs text-ink-500" title={item.url}>
            {item.url}
          </p>
        )}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => onRemove(item)}
        aria-label={`Remove ${item.label}`}
        className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-ink-500 transition-colors duration-200 hover:bg-rose-50 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </li>
  );
}

/**
 * One proposal row. Collapsed: client/campaign, status, share link, copy, status
 * select, delete. Expanded (on demand): fetches the FULL proposal (the list
 * endpoint only carries item_count) and lets the operator curate its items —
 * remove an item or add one (from the library or a custom link).
 */
function ProposalRow({
  proposal: p,
  assets,
  onStatusChange,
  onDelete,
  onItemCountChange,
}: {
  proposal: ProposalListItem;
  assets: CreativeAsset[];
  onStatusChange: (id: string, status: ProposalStatus) => void;
  onDelete: (id: string) => void;
  onItemCountChange: (id: string, count: number) => void;
}) {
  const url = proposalShareUrl(p.public_token);
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const panelId = `proposal-items-${p.id}`;
  const headerId = `proposal-header-${p.id}`;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await getProposal(p.id);
      setFull(data);
      onItemCountChange(p.id, data.items.length);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not load this proposal.');
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !full) void load();
  }

  async function removeItem(item: ProposalItem) {
    if (!full) return;
    if (!window.confirm(`Remove “${item.label}” from this proposal? Your client will no longer see it.`)) {
      return;
    }
    setRemovingId(item.id);
    setError(null);
    // Optimistic update, reconcile via the server count.
    const prev = full;
    const optimistic: Proposal = { ...full, items: full.items.filter((i) => i.id !== item.id) };
    setFull(optimistic);
    try {
      await removeProposalItem(p.id, item.id);
      onItemCountChange(p.id, optimistic.items.length);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setFull(prev); // roll back on failure
      setError(err instanceof ApiError ? err.message : 'Could not remove the item.');
    } finally {
      setRemovingId(null);
    }
  }

  function onAdded(updated: Proposal) {
    setFull(updated);
    onItemCountChange(p.id, updated.items.length);
  }

  // Library assets for THIS client (matched by client_name).
  const clientAssets = assets.filter((a) => a.client_name === p.client_name);
  const existingUrls = new Set((full?.items ?? []).map((i) => i.url));

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button
          type="button"
          id={headerId}
          onClick={toggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <svg
            viewBox="0 0 24 24"
            className={`h-4 w-4 flex-shrink-0 text-ink-500 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink">{p.client_name}</span>
            <span className="block truncate text-xs text-ink-500">
              {p.campaign_title} · {p.item_count} item{p.item_count === 1 ? '' : 's'}
            </span>
          </span>
        </button>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[p.status]}`}>
          {p.status}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 max-w-full truncate rounded-lg bg-surface-muted px-2 py-1 text-xs text-primary-700 hover:underline"
          title={url}
        >
          {url}
        </a>
        <CopyLinkButton url={url} />
        <select
          aria-label={`Status for ${p.client_name}`}
          value={p.status}
          onChange={(e) => onStatusChange(p.id, e.target.value as ProposalStatus)}
          className="cursor-pointer rounded-lg border border-line px-2 py-1 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onDelete(p.id)}
          className="cursor-pointer rounded-lg px-2 py-1 text-xs font-medium text-rose-600 transition-colors duration-200 hover:bg-rose-50"
        >
          Delete
        </button>
      </div>

      {open && (
        <div id={panelId} role="region" aria-labelledby={headerId} className="mt-3" aria-live="polite">
          {loading && <p className="text-xs text-ink-500">Loading items…</p>}
          {error && <p role="alert" className="text-xs text-rose-600">{error}</p>}
          {full && (
            <>
              {full.items.length === 0 ? (
                <p className="text-xs text-ink-500">No items yet — add one below.</p>
              ) : (
                <ul className="space-y-1.5">
                  {full.items
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((item) => (
                      <ProposalItemRow
                        key={item.id}
                        item={item}
                        busy={removingId === item.id}
                        onRemove={removeItem}
                      />
                    ))}
                </ul>
              )}
              <AddItemForm
                proposal={full}
                clientAssets={clientAssets}
                existingUrls={existingUrls}
                onAdded={onAdded}
              />
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function ProposalsList({
  proposals,
  assets,
  onStatusChange,
  onDelete,
  onItemCountChange,
}: {
  proposals: ProposalListItem[];
  assets: CreativeAsset[];
  onStatusChange: (id: string, status: ProposalStatus) => void;
  onDelete: (id: string) => void;
  onItemCountChange: (id: string, count: number) => void;
}) {
  if (proposals.length === 0) {
    return <p className="text-sm text-ink-500">No proposals yet. Create one above.</p>;
  }
  return (
    <ul className="divide-y divide-line">
      {proposals.map((p) => (
        <ProposalRow
          key={p.id}
          proposal={p}
          assets={assets}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          onItemCountChange={onItemCountChange}
        />
      ))}
    </ul>
  );
}

// --- generate pitch PDF (v3-1) -----------------------------------------------

/** Turn a free-typed client name into a safe slug segment (a-z0-9-_). */
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function Field({
  id,
  label,
  value,
  onChange,
  required,
  placeholder,
  hint,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  type?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink-700">
        {label}{' '}
        {!required && <span className="font-normal text-ink-500">(optional)</span>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {hint && <p className="mt-1 text-xs text-ink-500">{hint}</p>}
    </div>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
  required,
  rows = 3,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink-700">
        {label}
      </label>
      <textarea
        id={id}
        required={required}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}

function GeneratePitchPdfForm({
  onGenerated,
  regen,
}: {
  onGenerated: () => void;
  regen?: RegenContext;
}) {
  const init = (regen?.spec ?? {}) as Partial<GeneratePitchPdf>;
  // Essentials.
  const [clientName, setClientName] = useState(init.client_name ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(init.client_slug));
  const [clientSlug, setClientSlug] = useState(init.client_slug ?? '');
  const [title, setTitle] = useState(init.title ?? '');
  const [subtitle, setSubtitle] = useState(init.subtitle ?? '');
  const [prizeValue, setPrizeValue] = useState(init.prize_value ?? '');
  const [heroUrl, setHeroUrl] = useState(init.hero_image_url ?? '');
  // Key copy.
  const [vpHeadline, setVpHeadline] = useState(init.vp_headline ?? '');
  const [vpLead, setVpLead] = useState(init.vp_lead ?? '');
  const [prizeHead, setPrizeHead] = useState(init.prize_head ?? '');
  const [prizeText, setPrizeText] = useState(init.prize_text ?? '');
  const [closeHeadline, setCloseHeadline] = useState(init.close_headline ?? '');
  const [closeSub, setCloseSub] = useState(init.close_sub ?? '');

  const [showCopy, setShowCopy] = useState(
    Boolean(init.vp_headline || init.prize_head || init.close_headline),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Auto-derive the slug from the client name until the operator edits it.
  const effectiveSlug = slugTouched ? clientSlug : slugify(clientName);
  const filename = effectiveSlug ? `${effectiveSlug}-PITCH-generated` : '';

  function reset() {
    setClientName('');
    setSlugTouched(false);
    setClientSlug('');
    setTitle('');
    setSubtitle('');
    setPrizeValue('');
    setHeroUrl('');
    setVpHeadline('');
    setVpLead('');
    setPrizeHead('');
    setPrizeText('');
    setCloseHeadline('');
    setCloseSub('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);
    setBusy(true);
    try {
      // Build a complete, valid spec. Required copy blocks fall back to the
      // essentials so a minimal form still renders a full brochure; the engine
      // owns the rest of the layout.
      const head = prizeHead.trim() || `Win — ${title.trim() || clientName.trim()}`;
      const payload: GeneratePitchPdf = {
        client_name: clientName.trim(),
        client_slug: effectiveSlug,
        filename,
        title: title.trim(),
        subtitle: subtitle.trim(),
        prize_value: prizeValue.trim(),
        prize_label: 'Headline prize value',
        prize_icon: 'star',
        illustration: null,
        hero_image_url: heroUrl.trim() || null,
        cover_tags: [],
        vp_headline: vpHeadline.trim() || `A done-for-you campaign for ${clientName.trim()}.`,
        vp_lead:
          vpLead.trim() ||
          'We build, run and report a co-branded skill competition that puts your brand in front of thousands and routes every entrant to you as a consented lead.',
        vp_cards: [
          { icon: 'megaphone', t: 'Brand exposure', d: 'Thousands of impressions across social and our platform.' },
          { icon: 'leads', t: 'Consented leads', d: 'Warm, in-market prospects routed straight to you.' },
          { icon: 'chart', t: 'Board-ready numbers', d: 'Reach, entries, leads and cost-per-lead — measurable ROI.' },
          { icon: 'store', t: 'Foot traffic', d: 'A geo-targeted campaign that drives real visits.' },
        ],
        vp_steps: [
          { t: 'Apply', d: 'A 20-minute call to confirm the prize, branding and dates.' },
          { t: 'We build & run it', d: 'We design and launch the co-branded competition. Your team does nothing.' },
          { t: 'Leads + report land', d: 'Consented leads flow to you; a board-ready report lands at the end.' },
        ],
        prize_head: head,
        prize_text:
          prizeText.trim() ||
          'The winner is chosen on a published <strong>skill challenge, not a random draw</strong>. Free to enter, winner announced publicly, fully co-branded.',
        prize_pills: ['Skill-based', 'Free to enter', 'Winner announced publicly', 'Co-branded'],
        stats: [
          { num: 'R0', lbl: 'To become a partner' },
          { num: '100%', lbl: 'Consented leads' },
          { num: 'Done-for-you', lbl: 'We build & run it' },
        ],
        risk_reversal_t: 'R0 to become a partner — we share the risk.',
        risk_reversal_d:
          'No joining fee, no media spend. We fund the first campaigns while we build trust; every campaign stays open to negotiation. The upside is yours.',
        objections: [
          {
            q: 'Is this a lottery or a gamble?',
            a: 'No. The winner is chosen by a published skill challenge, not a random draw, and entry is free — keeping it inside the Consumer Protection Act’s promotional-competition rules.',
          },
          {
            q: 'What does it cost?',
            a: 'We’re flexible on funding — we fully fund the first few campaigns while we build trust, then each campaign is open to negotiation. Creative, mechanics and reporting are always on us.',
          },
        ],
        close_headline: closeHeadline.trim() || `Let’s build it with ${clientName.trim()}.`,
        close_sub:
          closeSub.trim() ||
          'A 20-minute call and we’ll bring a draft campaign built around your brand — no obligation.',
        close_cta: 'Book your 20-minute call',
        close_meta: 'Win a Prize — Corporate Partnerships',
        close_compliance:
          'Run as a skill-based promotional competition under the Consumer Protection Act s36: free-entry route, published rules and winner selection.',
      };

      if (regen) {
        const asset = (await regenerateAsset(regen.sourceId, payload)) as RegeneratedAsset;
        setOk(`Regenerated as v${asset.version ?? '?'} — “${asset.title}”. It’s now the current version in the library.`);
        onGenerated();
        regen.onDone?.();
      } else {
        const asset = await generatePitchPdf(payload);
        setOk(`Generated “${asset.title}” (${asset.pages} pages). It’s in the Creative Library.`);
        reset();
        onGenerated();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not generate the pitch PDF.');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    clientName.trim() && effectiveSlug && title.trim() && prizeValue.trim();

  return (
    <form onSubmit={submit} className="space-y-4">
      {regen && <RegenNote />}
      {ok && (
        <p role="status" className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
          {ok}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gp-client"
          label="Client name"
          required
          value={clientName}
          onChange={setClientName}
          placeholder="BMW Fourways"
        />
        <Field
          id="gp-slug"
          label="Client slug"
          required
          value={effectiveSlug}
          onChange={(v) => {
            setSlugTouched(true);
            setClientSlug(v);
          }}
          placeholder="bmw-fourways"
          hint={filename ? `Saves as generated/${effectiveSlug}/${filename}.pdf` : 'Letters, digits, - and _ only'}
        />
      </div>

      <Field
        id="gp-title"
        label="Cover title"
        required
        value={title}
        onChange={setTitle}
        placeholder="The BMW Fourways Showroom Challenge."
      />
      <TextArea
        id="gp-subtitle"
        label="Cover subtitle"
        value={subtitle}
        onChange={setSubtitle}
        rows={2}
        placeholder="Turn your hero vehicle into the city’s most talked-about giveaway…"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gp-prize"
          label="Prize value"
          required
          value={prizeValue}
          onChange={setPrizeValue}
          placeholder="R1,150,000"
        />
        <Field
          id="gp-hero"
          label="Hero image URL"
          type="url"
          value={heroUrl}
          onChange={setHeroUrl}
          placeholder="https://…/hero.jpg"
          hint="http(s) image embedded on the cover. Leave blank for the brand illustration."
        />
      </div>

      {/* Advanced copy — collapsible so the form isn't overwhelming. Sensible
          defaults are used for anything left blank. */}
      <div className="rounded-xl border border-line">
        <button
          type="button"
          onClick={() => setShowCopy((s) => !s)}
          aria-expanded={showCopy}
          className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold text-ink transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span>Customise the copy <span className="font-normal text-ink-500">(optional — smart defaults used otherwise)</span></span>
          <svg
            viewBox="0 0 24 24"
            className={`h-4 w-4 text-ink-500 transition-transform duration-200 ${showCopy ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {showCopy && (
          <div className="space-y-4 border-t border-line p-4">
            <Field id="gp-vph" label="Value-prop headline" value={vpHeadline} onChange={setVpHeadline} />
            <TextArea id="gp-vpl" label="Value-prop lead" value={vpLead} onChange={setVpLead} rows={3} />
            <Field id="gp-ph" label="Prize headline" value={prizeHead} onChange={setPrizeHead} />
            <TextArea id="gp-pt" label="Prize description" value={prizeText} onChange={setPrizeText} rows={3} />
            <Field id="gp-ch" label="Closing headline" value={closeHeadline} onChange={setCloseHeadline} />
            <TextArea id="gp-cs" label="Closing subtitle" value={closeSub} onChange={setCloseSub} rows={2} />
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !canSubmit}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-aurora px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
          </svg>
        )}
        {busy ? (regen ? 'Regenerating PDF…' : 'Generating PDF…') : regen ? 'Regenerate as new version' : 'Generate pitch PDF'}
      </button>
      {!canSubmit && (
        <p className="text-xs text-ink-500">Fill client, slug, title and prize value to generate.</p>
      )}
    </form>
  );
}

// --- generate video (ASYNC, v3-2) -------------------------------------------

const VIDEO_TYPES: { value: VideoType; label: string; blurb: string }[] = [
  { value: 'pitch', label: 'Pitch 16:9', blurb: 'Corporate pitch deck video for the client.' },
  { value: 'tvad', label: 'TV ad 16:9', blurb: 'Cinematic TV-style advert.' },
];

// Terminal job statuses — once here, polling stops.
function isTerminal(job: VideoRenderJob | null): boolean {
  return job?.status === 'done' || job?.status === 'failed';
}

/** Live job-status card: spinner while rendering, inline player on done. */
function VideoJobCard({ job }: { job: VideoRenderJob }) {
  if (job.status === 'done' && job.gcs_url) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" role="status">
        <p className="text-sm font-semibold text-emerald-700">Video ready — added to the Creative Library.</p>
        <div className="mt-3">
          <VideoPlayer
            src={job.gcs_url}
            label={job.creative_asset?.title ?? 'Generated video'}
            orientation={job.creative_asset?.orientation ?? '16x9'}
          />
        </div>
      </div>
    );
  }
  if (job.status === 'failed') {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" role="alert">
        <p className="text-sm font-semibold text-rose-700">The render failed.</p>
        <p className="mt-1 text-xs text-rose-600">
          {job.error || 'The video render service could not finish this job. Try again.'}
        </p>
      </div>
    );
  }
  // pending / rendering — animated waiting card (reserve space, no jump).
  const label = job.status === 'pending' ? 'Queued…' : 'Rendering your video…';
  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50 p-4" role="status" aria-live="polite">
      <div className="flex items-center gap-3">
        <svg className="h-5 w-5 animate-spin text-primary-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-primary-700">{label}</p>
          <p className="text-xs text-primary-600">
            Video render takes a few minutes — you can keep working; it’ll appear here and in the library when it’s done.
          </p>
        </div>
      </div>
    </div>
  );
}

export function GenerateVideoForm({
  onGenerated,
  regen,
}: {
  onGenerated: () => void;
  regen?: RegenContext;
}) {
  const init = (regen?.spec ?? {}) as Partial<GeneratePitchVideo>;
  const [videoType, setVideoType] = useState<VideoType>(init.video_type ?? 'pitch');
  const [clientName, setClientName] = useState(init.client_name ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(init.client_slug));
  const [clientSlug, setClientSlug] = useState(init.client_slug ?? '');
  const [dealerName, setDealerName] = useState(init.dealer_name ?? '');
  const [vehicle, setVehicle] = useState(init.vehicle ?? '');
  const [prizeValue, setPrizeValue] = useState(
    init.prize_value != null ? String(init.prize_value) : '',
  );
  const [heroUrl, setHeroUrl] = useState(init.hero_image_url ?? '');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<VideoRenderJob | null>(null);

  const effectiveSlug = slugTouched ? clientSlug : slugify(clientName);
  const tag = videoType === 'pitch' ? 'PITCH' : 'TVAD';
  const filename = effectiveSlug ? `${effectiveSlug}-${tag}-generated` : '';

  // Poll the job until it reaches a terminal state. Refresh the library on done.
  useEffect(() => {
    if (!job || isTerminal(job)) return;
    let cancelled = false;
    const id = window.setInterval(async () => {
      try {
        const next = await getVideoJob(job.id);
        if (cancelled) return;
        setJob(next);
        if (isTerminal(next)) {
          window.clearInterval(id);
          if (next.status === 'done') {
            onGenerated();
            regen?.onDone?.();
          }
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        // Transient poll error — keep trying on the next tick.
      }
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [job, onGenerated, regen]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setJob(null);
    setBusy(true);
    try {
      const payload: GeneratePitchVideo = {
        video_type: videoType,
        client_name: clientName.trim(),
        client_slug: effectiveSlug,
        filename,
        dealer_name: dealerName.trim() || clientName.trim(),
        vehicle: vehicle.trim(),
        prize_value: Number(prizeValue) || 0,
        catchment: videoType === 'pitch' ? 'your area & the surrounds' : null,
        hero_image_url: videoType === 'pitch' ? heroUrl.trim() || null : null,
      };
      const started = regen
        ? ((await regenerateAsset(regen.sourceId, payload)) as VideoRenderJob)
        : await generatePitchVideo(payload);
      setJob(started);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not start the video render.');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    clientName.trim() && effectiveSlug && vehicle.trim() && Number(prizeValue) > 0;

  return (
    <form onSubmit={submit} className="space-y-4">
      {regen && <RegenNote />}
      {/* Video-type segmented toggle. */}
      <div>
        <span className="block text-sm font-medium text-ink-700">Video type</span>
        <div className="mt-1 inline-flex rounded-xl border border-line p-1" role="tablist" aria-label="Video type">
          {VIDEO_TYPES.map((t) => {
            const active = videoType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setVideoType(t.value)}
                className={[
                  'cursor-pointer rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  active ? 'bg-aurora text-white shadow-cta' : 'text-ink-700 hover:bg-surface-muted',
                ].join(' ')}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-ink-500">{VIDEO_TYPES.find((t) => t.value === videoType)?.blurb}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gv-client"
          label="Client name"
          required
          value={clientName}
          onChange={setClientName}
          placeholder="BMW Fourways"
        />
        <Field
          id="gv-slug"
          label="Client slug"
          required
          value={effectiveSlug}
          onChange={(v) => {
            setSlugTouched(true);
            setClientSlug(v);
          }}
          placeholder="bmw-fourways"
          hint={filename ? `Saves as generated/${effectiveSlug}/${filename}.mp4` : 'Letters, digits, - and _ only'}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gv-dealer"
          label="Dealer / client name (on-screen)"
          value={dealerName}
          onChange={setDealerName}
          placeholder="BMW Fourways"
          hint="Defaults to the client name if blank."
        />
        <Field
          id="gv-vehicle"
          label="Prize vehicle"
          required
          value={vehicle}
          onChange={setVehicle}
          placeholder="BMW M240i"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gv-prize"
          label="Prize value (ZAR)"
          required
          type="number"
          value={prizeValue}
          onChange={setPrizeValue}
          placeholder="1150000"
        />
        {videoType === 'pitch' && (
          <Field
            id="gv-hero"
            label="Hero image URL"
            type="url"
            value={heroUrl}
            onChange={setHeroUrl}
            placeholder="https://…/hero.jpg"
            hint="http(s) image used on the pitch. Leave blank for the brand default."
          />
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !canSubmit}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-aurora px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
          </svg>
        )}
        {busy ? 'Starting render…' : regen ? 'Regenerate as new version' : 'Generate video'}
      </button>
      {!canSubmit && (
        <p className="text-xs text-ink-500">Fill client, slug, vehicle and prize value to generate.</p>
      )}

      {/* Live job status — reserved below the form so submitting doesn't jump. */}
      {job && (
        <div className="pt-2">
          <VideoJobCard job={job} />
        </div>
      )}
    </form>
  );
}

// --- generate image (SYNCHRONOUS, v3-3) --------------------------------------

const IMAGE_ASPECTS: { value: ImageAspect; label: string; blurb: string }[] = [
  { value: '16:9', label: '16:9', blurb: 'Landscape hero — pairs with the PDF cover & pitch video.' },
  { value: '1:1', label: '1:1', blurb: 'Square — social posts & avatars.' },
  { value: '9:16', label: '9:16', blurb: 'Portrait — stories & vertical video.' },
];

export function GenerateImageForm({
  onGenerated,
  regen,
}: {
  onGenerated: () => void;
  regen?: RegenContext;
}) {
  const init = (regen?.spec ?? {}) as Partial<GeneratePitchImage>;
  const [clientName, setClientName] = useState(init.client_name ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(init.client_slug));
  const [clientSlug, setClientSlug] = useState(init.client_slug ?? '');
  const [prompt, setPrompt] = useState(init.prompt ?? '');
  const [aspect, setAspect] = useState<ImageAspect>(init.aspect ?? '16:9');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CreativeAsset | null>(null);

  const effectiveSlug = slugTouched ? clientSlug : slugify(clientName);
  const filename = effectiveSlug ? `${effectiveSlug}-hero-generated` : '';

  function reset() {
    setClientName('');
    setSlugTouched(false);
    setClientSlug('');
    setPrompt('');
    setAspect('16:9');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      const payload: GeneratePitchImage = {
        client_name: clientName.trim(),
        client_slug: effectiveSlug,
        filename,
        prompt: prompt.trim(),
        aspect,
      };
      if (regen) {
        const asset = (await regenerateAsset(regen.sourceId, payload)) as AssetVersion;
        setDone(asset);
        onGenerated();
        regen.onDone?.();
      } else {
        const asset = await generatePitchImage(payload);
        setDone(asset);
        reset();
        onGenerated();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not generate the image.');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = clientName.trim() && effectiveSlug && prompt.trim();

  return (
    <form onSubmit={submit} className="space-y-4">
      {regen && <RegenNote />}
      {/* Aspect-ratio segmented toggle. */}
      <div>
        <span className="block text-sm font-medium text-ink-700">Aspect ratio</span>
        <div className="mt-1 inline-flex rounded-xl border border-line p-1" role="tablist" aria-label="Aspect ratio">
          {IMAGE_ASPECTS.map((a) => {
            const active = aspect === a.value;
            return (
              <button
                key={a.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setAspect(a.value)}
                className={[
                  'cursor-pointer rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  active ? 'bg-aurora text-white shadow-cta' : 'text-ink-700 hover:bg-surface-muted',
                ].join(' ')}
              >
                {a.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-ink-500">{IMAGE_ASPECTS.find((a) => a.value === aspect)?.blurb}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gi-client"
          label="Client name"
          required
          value={clientName}
          onChange={setClientName}
          placeholder="BMW Fourways"
        />
        <Field
          id="gi-slug"
          label="Client slug"
          required
          value={effectiveSlug}
          onChange={(v) => {
            setSlugTouched(true);
            setClientSlug(v);
          }}
          placeholder="bmw-fourways"
          hint={filename ? `Saves as generated-images/${effectiveSlug}/${filename}.png` : 'Letters, digits, - and _ only'}
        />
      </div>

      <TextArea
        id="gi-prompt"
        label="Image prompt"
        required
        rows={4}
        value={prompt}
        onChange={setPrompt}
        placeholder="Professional automotive advertising photograph, a sleek blue BMW M240i coupe in a bright premium showroom, dramatic studio lighting with a soft violet-pink glow. Cinematic, high-end car campaign. No text, no logos."
      />

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !canSubmit}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-aurora px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
          </svg>
        )}
        {busy ? (regen ? 'Regenerating image…' : 'Generating image…') : regen ? 'Regenerate as new version' : 'Generate image'}
      </button>
      {!canSubmit && (
        <p className="text-xs text-ink-500">Fill client, slug and a prompt to generate.</p>
      )}

      {/* On success: an inline preview + copy-hero-URL right here, plus it lands
          in the Creative Library. */}
      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" role="status">
          <p className="text-sm font-semibold text-emerald-700">
            Image ready — added to the Creative Library.
          </p>
          <p className="mt-0.5 text-xs text-emerald-700/80">
            Copy the hero URL and paste it into the Generate Pitch PDF / Generate Video hero field.
          </p>
          <div className="mt-3">
            <ImagePreview src={done.gcs_url} label={done.title} />
          </div>
        </div>
      )}
    </form>
  );
}

// --- generate voiceover (SYNCHRONOUS, v3-4) ----------------------------------

const VOICES: { value: Voice; label: string; blurb: string }[] = [
  { value: 'ash', label: 'Ash', blurb: 'Warm, confident — great for pitch narration.' },
  { value: 'nova', label: 'Nova', blurb: 'Bright, upbeat — great for ad reads.' },
  { value: 'onyx', label: 'Onyx', blurb: 'Deep, authoritative — corporate gravitas.' },
  { value: 'sage', label: 'Sage', blurb: 'Calm, measured — explainer style.' },
  { value: 'alloy', label: 'Alloy', blurb: 'Neutral, versatile all-rounder.' },
  { value: 'shimmer', label: 'Shimmer', blurb: 'Soft, friendly — approachable tone.' },
];

export function GenerateVoiceoverForm({
  onGenerated,
  regen,
}: {
  onGenerated: () => void;
  regen?: RegenContext;
}) {
  const init = (regen?.spec ?? {}) as Partial<GeneratePitchVoiceover>;
  const [clientName, setClientName] = useState(init.client_name ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(init.client_slug));
  const [clientSlug, setClientSlug] = useState(init.client_slug ?? '');
  const [text, setText] = useState(init.text ?? '');
  const [voice, setVoice] = useState<Voice>(init.voice ?? 'ash');
  const [instructions, setInstructions] = useState(init.instructions ?? '');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CreativeAsset | null>(null);

  const effectiveSlug = slugTouched ? clientSlug : slugify(clientName);
  const filename = effectiveSlug ? `${effectiveSlug}-voiceover` : '';

  function reset() {
    setClientName('');
    setSlugTouched(false);
    setClientSlug('');
    setText('');
    setVoice('ash');
    setInstructions('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      const payload: GeneratePitchVoiceover = {
        client_name: clientName.trim(),
        client_slug: effectiveSlug,
        filename,
        text: text.trim(),
        voice,
        ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
      };
      if (regen) {
        const asset = (await regenerateAsset(regen.sourceId, payload)) as AssetVersion;
        setDone(asset);
        onGenerated();
        regen.onDone?.();
      } else {
        const asset = await generatePitchVoiceover(payload);
        setDone(asset);
        reset();
        onGenerated();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not generate the voiceover.');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = clientName.trim() && effectiveSlug && text.trim();

  return (
    <form onSubmit={submit} className="space-y-4">
      {regen && <RegenNote />}
      {/* Voice picker. */}
      <div>
        <label htmlFor="gv-voice" className="block text-sm font-medium text-ink-700">
          Voice
        </label>
        <select
          id="gv-voice"
          value={voice}
          onChange={(e) => setVoice(e.target.value as Voice)}
          className="mt-1 w-full cursor-pointer rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {VOICES.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-ink-500">{VOICES.find((v) => v.value === voice)?.blurb}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="gv-client"
          label="Client name"
          required
          value={clientName}
          onChange={setClientName}
          placeholder="BMW Fourways"
        />
        <Field
          id="gv-slug"
          label="Client slug"
          required
          value={effectiveSlug}
          onChange={(v) => {
            setSlugTouched(true);
            setClientSlug(v);
          }}
          placeholder="bmw-fourways"
          hint={filename ? `Saves as generated-audio/${effectiveSlug}/${filename}.mp3` : 'Letters, digits, - and _ only'}
        />
      </div>

      <TextArea
        id="gv-text"
        label="Voiceover script"
        required
        rows={5}
        value={text}
        onChange={setText}
        placeholder="Imagine your showroom becoming the most talked-about place in the city. At BMW Fourways, we turn your hero car into a skill-based competition…"
      />

      <TextArea
        id="gv-instructions"
        label="Tone / delivery instructions"
        rows={2}
        value={instructions}
        onChange={setInstructions}
        placeholder="Warm, confident, friendly podcast host. Natural conversational pace, genuinely enthusiastic but never pushy."
      />

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !canSubmit}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-aurora px-5 py-2.5 text-sm font-semibold text-white shadow-cta transition-opacity duration-200 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
          </svg>
        )}
        {busy ? (regen ? 'Regenerating voiceover…' : 'Generating voiceover…') : regen ? 'Regenerate as new version' : 'Generate voiceover'}
      </button>
      {!canSubmit && (
        <p className="text-xs text-ink-500">Fill client, slug and a script to generate.</p>
      )}

      {/* On success: an inline player right here, plus it lands in the library. */}
      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" role="status">
          <p className="text-sm font-semibold text-emerald-700">
            Voiceover ready — added to the Creative Library.
          </p>
          <div className="mt-3">
            <AudioPlayer src={done.gcs_url} label={done.title} />
          </div>
        </div>
      )}
    </form>
  );
}

// --- version history drawer + compare (v3-5) ---------------------------------

/** A compact version row in the history drawer: preview + meta + actions. */
function VersionRow({
  version,
  selectedForCompare,
  onToggleCompare,
  onSetCurrent,
  busy,
}: {
  version: AssetVersion;
  selectedForCompare: boolean;
  onToggleCompare: (v: AssetVersion) => void;
  onSetCurrent: (v: AssetVersion) => void;
  busy: boolean;
}) {
  return (
    <div
      className={[
        'rounded-xl border p-3 transition-colors duration-200',
        version.is_current ? 'border-primary bg-primary-50/40' : 'border-line bg-white',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tag tone="warm">v{version.version}</Tag>
          {version.is_current && <Tag tone="accent">Current</Tag>}
          <span className="text-xs text-ink-500">
            {new Date(version.created_at).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-ink-700">
            <input
              type="checkbox"
              checked={selectedForCompare}
              onChange={() => onToggleCompare(version)}
              className="h-4 w-4 cursor-pointer rounded border-line text-primary focus:ring-primary"
              aria-label={`Compare version ${version.version}`}
            />
            Compare
          </label>
          {!version.is_current && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onSetCurrent(version)}
              className="cursor-pointer rounded-lg border border-primary-200 bg-white px-2.5 py-1 text-xs font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Set current
            </button>
          )}
        </div>
      </div>
      <div className="mt-3">
        <MediaPreview asset={version} />
      </div>
    </div>
  );
}

export function VersionHistoryDrawer({
  lineageId,
  reloadKey,
  onClose,
  onChanged,
}: {
  lineageId: string;
  reloadKey: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [lineage, setLineage] = useState<AssetLineage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState<AssetVersion[]>([]);

  useEffect(() => {
    let cancelled = false;
    getLineage(lineageId)
      .then((l) => {
        if (cancelled) return;
        setLineage(l);
        // Keep compare selection valid against the refreshed list.
        setCompare((cur) =>
          cur
            .map((c) => l.versions.find((v) => v.id === c.id))
            .filter((v): v is AssetVersion => Boolean(v)),
        );
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof ApiError ? err.message : 'Could not load the version history.');
      });
    return () => {
      cancelled = true;
    };
  }, [lineageId, reloadKey]);

  function toggleCompare(v: AssetVersion) {
    setCompare((cur) => {
      if (cur.some((c) => c.id === v.id)) return cur.filter((c) => c.id !== v.id);
      // Keep the two most-recently picked.
      const next = [...cur, v];
      return next.slice(-2);
    });
  }

  async function setCurrent(v: AssetVersion) {
    setBusy(true);
    setError(null);
    try {
      await setCurrentVersion(v.id);
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not set this version as current.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Version history">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close version history"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-ink/30"
      />
      <aside className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-card">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-white/95 px-5 py-4 backdrop-blur">
          <div>
            <h2 className="font-heading text-base font-bold text-ink">Version history</h2>
            <p className="mt-0.5 text-xs text-ink-500">
              Every version of this asset. Revert to any one, or tick two to compare.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer rounded-lg p-1.5 text-ink-500 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 p-5">
          {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
          {!lineage && !error && <p className="text-sm text-ink-500">Loading…</p>}

          {/* Compare panel — two versions side by side. */}
          {compare.length === 2 && (
            <div className="rounded-2xl border border-primary-200 bg-primary-50/40 p-4">
              <p className="mb-3 font-heading text-sm font-bold text-ink">
                Comparing v{compare[0].version} vs v{compare[1].version}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {compare.map((v) => (
                  <div key={v.id} className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Tag tone="warm">v{v.version}</Tag>
                      {v.is_current && <Tag tone="accent">Current</Tag>}
                    </div>
                    <MediaPreview asset={v} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {compare.length === 1 && (
            <p className="rounded-lg bg-surface-muted px-3 py-2 text-xs text-ink-500">
              Tick one more version to compare side by side.
            </p>
          )}

          {lineage?.versions
            .slice()
            .sort((a, b) => b.version - a.version)
            .map((v) => (
              <VersionRow
                key={v.id}
                version={v}
                selectedForCompare={compare.some((c) => c.id === v.id)}
                onToggleCompare={toggleCompare}
                onSetCurrent={setCurrent}
                busy={busy}
              />
            ))}
        </div>
      </aside>
    </div>
  );
}

// --- edit & regenerate modal (v3-5) ------------------------------------------

const KIND_LABEL: Record<string, string> = {
  pdf: 'Edit & regenerate — Pitch PDF',
  video: 'Edit & regenerate — Video',
  image: 'Edit & regenerate — Hero image',
  voiceover: 'Edit & regenerate — Voiceover',
};

function EditRegenerateModal({
  asset,
  onClose,
  onGenerated,
}: {
  asset: CreativeAsset;
  onClose: () => void;
  onGenerated: () => void;
}) {
  const kind = asset.generation_kind ?? 'manual';
  const spec = (asset.generation_spec as Record<string, unknown> | undefined) ?? {};
  const regen: RegenContext = { sourceId: asset.id, spec, onDone: onClose };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6" role="dialog" aria-modal="true" aria-label="Edit and regenerate">
      <button
        type="button"
        aria-label="Close editor"
        onClick={onClose}
        className="fixed inset-0 cursor-pointer bg-ink/30"
      />
      <div className="relative my-4 w-full max-w-2xl rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-bold text-ink">
              {KIND_LABEL[kind] ?? 'Edit & regenerate'}
            </h2>
            <p className="mt-0.5 text-sm text-ink-500">
              {asset.title} — currently v{asset.version ?? 1}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer rounded-lg p-1.5 text-ink-500 transition-colors duration-200 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {kind === 'pdf' && <GeneratePitchPdfForm onGenerated={onGenerated} regen={regen} />}
        {kind === 'image' && <GenerateImageForm onGenerated={onGenerated} regen={regen} />}
        {kind === 'video' && <GenerateVideoForm onGenerated={onGenerated} regen={regen} />}
        {kind === 'voiceover' && <GenerateVoiceoverForm onGenerated={onGenerated} regen={regen} />}
      </div>
    </div>
  );
}

// --- one-click full pack (v3-6) ----------------------------------------------

const PACK_OUTPUTS: { key: keyof PackInclude; label: string; hint: string }[] = [
  { key: 'image', label: 'Hero image', hint: 'Generated first — reused as the hero for the PDF & video.' },
  { key: 'pdf', label: 'Pitch PDF', hint: 'Branded pitch deck.' },
  { key: 'voiceover', label: 'Voiceover', hint: 'TTS narration of the offer.' },
  { key: 'video_pitch', label: 'Pitch video', hint: 'Renders in the background.' },
  { key: 'video_tvad', label: 'TV ad video', hint: 'Renders in the background.' },
];

const ASSET_OUTPUT_LABEL: Record<string, string> = {
  image: 'Hero image',
  pdf: 'Pitch PDF',
  voiceover: 'Voiceover',
  video_pitch: 'Pitch video',
  video_tvad: 'TV ad video',
};

/** Live per-output status list for a completed pack run (videos keep polling). */
function PackResultPanel({ result }: { result: GeneratePackResponse }) {
  // Track each kicked video job so the panel reflects render progress.
  const [jobs, setJobs] = useState<Record<string, VideoRenderJob | null>>({});

  useEffect(() => {
    const kicked = result.video_jobs.filter((v) => v.status === 'kicked' && v.job_id);
    if (kicked.length === 0) return;
    let cancelled = false;
    const id = window.setInterval(async () => {
      for (const v of kicked) {
        try {
          const job = await getVideoJob(v.job_id as string);
          if (cancelled) return;
          setJobs((cur) => ({ ...cur, [v.output]: job }));
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) return;
        }
      }
      // Stop once every tracked job is terminal.
      setJobs((cur) => {
        const allDone = kicked.every((v) => {
          const j = cur[v.output];
          return j && (j.status === 'done' || j.status === 'failed');
        });
        if (allDone) window.clearInterval(id);
        return cur;
      });
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [result]);

  return (
    <div className="space-y-3" data-testid="pack-result">
      <ul className="space-y-2">
        {result.assets.map((a) => (
          <li
            key={a.output}
            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${
              a.status === 'done'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-rose-200 bg-rose-50 text-rose-700'
            }`}
          >
            <span className="font-medium">{ASSET_OUTPUT_LABEL[a.output] ?? a.output}</span>
            <span className="text-xs">
              {a.status === 'done' ? 'Done — in the library' : `Failed — ${a.error ?? 'try again'}`}
            </span>
          </li>
        ))}
        {result.video_jobs.map((v) => {
          const job = jobs[v.output];
          let text: string;
          let tone: 'ok' | 'wait' | 'fail';
          if (v.status === 'failed') {
            tone = 'fail';
            text = `Failed — ${v.error ?? 'try again'}`;
          } else if (job?.status === 'done') {
            tone = 'ok';
            text = 'Done — in the library';
          } else if (job?.status === 'failed') {
            tone = 'fail';
            text = job.error || 'Render failed.';
          } else {
            tone = 'wait';
            text = 'Rendering in the background…';
          }
          const cls =
            tone === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : tone === 'fail'
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-primary-200 bg-primary-50 text-primary-700';
          return (
            <li
              key={v.output}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${cls}`}
            >
              <span className="font-medium">{ASSET_OUTPUT_LABEL[v.output] ?? v.output}</span>
              <span className="flex items-center gap-2 text-xs">
                {tone === 'wait' && (
                  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
                  </svg>
                )}
                {text}
              </span>
            </li>
          );
        })}
      </ul>

      {result.proposal && (
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-3">
          <p className="text-sm font-semibold text-primary-700">Draft proposal created.</p>
          {result.proposal.videos_pending && (
            <p className="mt-0.5 text-xs text-primary-600">
              Videos are still rendering — drop them in from the library once their jobs finish.
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <a
              href={proposalShareUrl(result.proposal.public_token)}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 max-w-full truncate text-xs text-primary-700 underline"
              title={proposalShareUrl(result.proposal.public_token)}
            >
              {proposalShareUrl(result.proposal.public_token)}
            </a>
            <CopyLinkButton url={proposalShareUrl(result.proposal.public_token)} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The capstone "Generate full pack" form: one submit produces a client's whole
 * pack (image/pdf/voiceover/videos) + an optional draft proposal. Reuses the
 * shared Field/TextArea inputs, the existing job polling (via PackResultPanel)
 * and CopyLinkButton.
 */
export function GenerateFullPackForm({ onGenerated }: { onGenerated: () => void }) {
  const [clientName, setClientName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [clientSlug, setClientSlug] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [prizeValue, setPrizeValue] = useState('');
  const [heroUrl, setHeroUrl] = useState('');
  const [include, setInclude] = useState<PackInclude>({
    image: true,
    pdf: true,
    voiceover: true,
    video_pitch: true,
    video_tvad: false,
  });
  const [createProp, setCreateProp] = useState(true);
  const [intro, setIntro] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratePackResponse | null>(null);

  const effectiveSlug = slugTouched ? clientSlug : slugify(clientName);
  const anySelected = Object.values(include).some(Boolean);

  function toggle(key: keyof PackInclude) {
    setInclude((cur) => ({ ...cur, [key]: !cur[key] }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    const value = Number(prizeValue);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a prize value greater than zero.');
      return;
    }
    if (!anySelected) {
      setError('Pick at least one output to generate.');
      return;
    }
    const payload: GeneratePack = {
      client_name: clientName.trim(),
      client_slug: effectiveSlug,
      vehicle_or_prize: vehicle.trim(),
      prize_value: Math.round(value),
      hero_image_url: heroUrl.trim() || null,
      include,
      create_proposal: createProp,
      intro: intro.trim() || undefined,
    };
    setBusy(true);
    try {
      const res = await generatePack(payload);
      setResult(res);
      onGenerated(); // refresh the library — sync assets are already there
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not generate the pack.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" aria-label="Generate full pack">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          id="pack-client"
          label="Client name"
          value={clientName}
          onChange={setClientName}
          required
          placeholder="BMW Fourways"
        />
        <Field
          id="pack-slug"
          label="Client slug"
          value={effectiveSlug}
          onChange={(v) => {
            setSlugTouched(true);
            setClientSlug(v);
          }}
          required
          hint="GCS folder + filename segment."
        />
        <Field
          id="pack-vehicle"
          label="Vehicle / prize"
          value={vehicle}
          onChange={setVehicle}
          required
          placeholder="BMW M240i"
        />
        <Field
          id="pack-value"
          label="Prize value"
          value={prizeValue}
          onChange={setPrizeValue}
          required
          type="number"
          placeholder="1150000"
        />
      </div>
      <Field
        id="pack-hero"
        label="Hero image URL"
        value={heroUrl}
        onChange={setHeroUrl}
        placeholder="https://… (or leave blank to generate one)"
        hint="If you tick “Hero image”, the generated one is used and this is ignored."
      />

      <fieldset>
        <legend className="text-sm font-medium text-ink-700">Outputs to generate</legend>
        <div className="mt-2 space-y-2">
          {PACK_OUTPUTS.map((o) => (
            <label key={o.key} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={include[o.key]}
                onChange={() => toggle(o.key)}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded border-line text-primary focus:ring-primary"
                aria-label={o.label}
              />
              <span>
                <span className="font-medium text-ink-700">{o.label}</span>{' '}
                <span className="text-xs text-ink-500">— {o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={createProp}
          onChange={() => setCreateProp((v) => !v)}
          className="h-4 w-4 cursor-pointer rounded border-line text-primary focus:ring-primary"
          aria-label="Create draft proposal"
        />
        <span className="font-medium text-ink-700">Assemble a draft proposal</span>
      </label>
      {createProp && (
        <TextArea
          id="pack-intro"
          label="Proposal intro (optional)"
          value={intro}
          onChange={setIntro}
          rows={2}
          placeholder="A short note shown at the top of the client's proposal."
        />
      )}

      {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Generating pack…' : 'Generate full pack'}
      </button>

      {result && <PackResultPanel result={result} />}
    </form>
  );
}

// --- generate full pack FROM A MANIFEST (v5-4) -------------------------------
//
// One manifest → a client's whole pack: the requested videos (rendered in the
// background), a branded PDF, and a stitched draft proposal. Every image field
// MUST be a https://storage.googleapis.com/... URL — the backend rejects
// non-GCS URLs (422). On an incomplete manifest the 422 carries a per-format
// `missing[]` list, which we surface so the operator knows which slot to fill.

/** The industries the manifest generator presets recognise. */
const MANIFEST_INDUSTRIES: { value: ManifestIndustry; label: string }[] = [
  { value: 'dental', label: 'Dental' },
  { value: 'medical', label: 'Medical' },
  { value: 'cosmetic-surgery', label: 'Cosmetic surgery' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'media-broadcast', label: 'Media broadcast' },
  { value: 'retail', label: 'Retail' },
  { value: 'generic', label: 'Generic' },
];

/** Human labels for each output format checkbox. */
const MANIFEST_FORMAT_LABELS: Record<ManifestFormat, { label: string; hint: string }> = {
  pitchad: { label: 'Pitch ad video', hint: 'Short pitch ad — renders in the background.' },
  pitchadv: { label: 'Pitch ad (vertical)', hint: '9:16 social cut — renders in the background.' },
  showcase: { label: 'Showcase video', hint: 'Longer showcase — renders in the background.' },
  pdf_brochure: { label: 'PDF brochure', hint: 'Branded brochure — generated in-request.' },
};

/** The six scene URL slots (handover is optional). */
const MANIFEST_SCENES: { key: keyof ClientManifest['scenes']; label: string; required: boolean }[] = [
  { key: 'hero', label: 'Hero scene', required: true },
  { key: 'create', label: 'Create scene', required: true },
  { key: 'conv', label: 'Conversation scene', required: true },
  { key: 'browse', label: 'Browse scene', required: true },
  { key: 'winner', label: 'Winner scene', required: true },
  { key: 'handover', label: 'Handover scene', required: false },
];

/** The dentist-cyan defaults the manifest brand palette falls back to. */
const DEFAULT_BRAND: ManifestBrand = {
  primary: '#22d3ee',
  accent: '#0ea5e9',
  warm: '#f97316',
  ink: '#0f172a',
  muted: '#64748b',
};

const BRAND_FIELDS: { key: keyof ManifestBrand; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'accent', label: 'Accent' },
  { key: 'warm', label: 'Warm' },
  { key: 'ink', label: 'Ink' },
  { key: 'muted', label: 'Muted' },
];

/** A GCS https URL or empty (so we can require-on-submit with a clear message). */
function isGcsUrl(url: string): boolean {
  return /^https:\/\/storage\.googleapis\.com\//i.test(url.trim());
}

/** Pretty per-format label, falling back to the raw value for unknown formats. */
function formatLabel(fmt: string): string {
  return (MANIFEST_FORMAT_LABELS as Record<string, { label: string }>)[fmt]?.label ?? fmt;
}

/** Result card for a manifest pack run: proposal link + PDF + per-video status. */
function ManifestResultPanel({ result }: { result: GeneratePackFromManifestResponse }) {
  const shareUrl = proposalShareUrl(result.proposal.public_token);
  return (
    <div className="space-y-3" data-testid="manifest-result">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm font-semibold text-emerald-700">
          Pack generated for {result.sponsor_name}.
        </p>
        {result.note && <p className="mt-0.5 text-xs text-emerald-700">{result.note}</p>}
      </div>

      {/* Proposal share link */}
      <div className="rounded-xl border border-primary-200 bg-primary-50 p-3">
        <p className="text-sm font-semibold text-primary-700">
          Draft proposal — {result.proposal.item_count} item
          {result.proposal.item_count === 1 ? '' : 's'} ({result.proposal.status}).
        </p>
        {result.proposal.videos_pending && (
          <p className="mt-0.5 text-xs text-primary-600">
            Videos are still rendering — they'll appear in the proposal once their jobs finish.
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 max-w-full truncate text-xs text-primary-700 underline"
            title={shareUrl}
          >
            {shareUrl}
          </a>
          <CopyLinkButton url={shareUrl} />
        </div>
      </div>

      {/* PDF status */}
      <div
        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${
          result.pdf.status === 'done'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-rose-200 bg-rose-50 text-rose-700'
        }`}
      >
        <span className="font-medium">PDF brochure</span>
        <span className="flex items-center gap-2 text-xs">
          {result.pdf.status === 'done'
            ? `Done${result.pdf.pages ? ` — ${result.pdf.pages} pages` : ''}`
            : `Failed — ${result.pdf.error ?? 'try again'}`}
          {result.pdf.status === 'done' && result.pdf.gcs_url && isSafeHttpUrl(result.pdf.gcs_url) && (
            <a
              href={result.pdf.gcs_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Open
            </a>
          )}
        </span>
      </div>

      {/* Per-video job status */}
      <ul className="space-y-2">
        {result.videos.map((v) => {
          const tone =
            v.status === 'failed' ? 'fail' : v.status === 'kicked' || v.status === 'queued' ? 'wait' : 'ok';
          const cls =
            tone === 'fail'
              ? 'border-rose-200 bg-rose-50 text-rose-700'
              : tone === 'wait'
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700';
          const text =
            v.status === 'failed'
              ? `Failed — ${v.error ?? 'try again'}`
              : v.status === 'kicked' || v.status === 'queued'
                ? 'Rendering in the background…'
                : 'Done — in the library';
          return (
            <li
              key={v.format}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${cls}`}
            >
              <span className="font-medium">{formatLabel(v.format)}</span>
              <span className="flex items-center gap-2 text-xs">
                {tone === 'wait' && (
                  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4z" />
                  </svg>
                )}
                {text}
                {v.final_gcs_url && isSafeHttpUrl(v.final_gcs_url) && (
                  <a
                    href={v.final_gcs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Open
                  </a>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The 422 incomplete-manifest panel: per-format list of missing asset slots. */
function ManifestMissingPanel({ missing }: { missing: ManifestMissingEntry[] }) {
  return (
    <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-800">
        Manifest incomplete — fill these asset slots and resubmit:
      </p>
      <ul className="mt-2 space-y-2">
        {missing.map((m) => (
          <li key={m.format} className="text-sm text-amber-800">
            <span className="font-semibold">{formatLabel(m.format)}</span>
            <ul className="ml-4 mt-0.5 list-disc text-xs">
              {m.missing.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Generate a client's whole pack from a manifest. One submit POSTs a full
 * ClientManifest to the staff-gated generate-pack-from-manifest endpoint, then
 * shows the proposal share link, the PDF status and the per-video job statuses.
 * Every image field must be a GCS https URL (validated before submit + by the
 * backend). On a 422 incomplete manifest the per-format missing[] list is shown.
 */
export function GeneratePackFromManifestForm({ onGenerated }: { onGenerated: () => void }) {
  // Identity
  const [sponsorName, setSponsorName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slug, setSlug] = useState('');
  const [industry, setIndustry] = useState<ManifestIndustry>('dental');
  const [prizeLabel, setPrizeLabel] = useState('');
  const [prizeShort, setPrizeShort] = useState('');
  const [prizeSpoken, setPrizeSpoken] = useState('');
  const [prizeValueLabel, setPrizeValueLabel] = useState('');
  const [url, setUrl] = useState('');
  const [registerUrl, setRegisterUrl] = useState('');

  // Scenes (six slots, handover optional)
  const [scenes, setScenes] = useState<Record<string, string>>({});

  // Page / QR / prize images
  const [pageImage, setPageImage] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [prizeImage, setPrizeImage] = useState('');

  // Business
  const [bizImage, setBizImage] = useState('');
  const [bizAddress, setBizAddress] = useState('');
  const [bizRating, setBizRating] = useState('');
  const [bizReviews, setBizReviews] = useState('');

  // Brand (defaults to the dentist-cyan palette)
  const [brand, setBrand] = useState<ManifestBrand>({ ...DEFAULT_BRAND });

  // Formats (default all four)
  const [formats, setFormats] = useState<Set<ManifestFormat>>(new Set(MANIFEST_FORMATS));

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<ManifestMissingEntry[] | null>(null);
  const [result, setResult] = useState<GeneratePackFromManifestResponse | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(sponsorName);

  function setScene(key: string, value: string) {
    setScenes((cur) => ({ ...cur, [key]: value }));
  }

  function toggleFormat(fmt: ManifestFormat) {
    setFormats((cur) => {
      const next = new Set(cur);
      if (next.has(fmt)) next.delete(fmt);
      else next.add(fmt);
      return next;
    });
  }

  function setBrandField(key: keyof ManifestBrand, value: string) {
    setBrand((cur) => ({ ...cur, [key]: value }));
  }

  /** Collect every image field that is non-empty but NOT a GCS https URL. */
  function badGcsFields(): string[] {
    const bad: string[] = [];
    const check = (label: string, value: string, req: boolean) => {
      const v = value.trim();
      if (!v) {
        if (req) bad.push(`${label} (required)`);
        return;
      }
      if (!isGcsUrl(v)) bad.push(`${label} must be a storage.googleapis.com URL`);
    };
    for (const s of MANIFEST_SCENES) check(`${s.label} URL`, scenes[s.key] ?? '', s.required);
    check('Page image', pageImage, true);
    check('QR image', qrImage, true);
    check('Prize image', prizeImage, false);
    check('Business image', bizImage, true);
    return bad;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMissing(null);
    setResult(null);

    if (formats.size === 0) {
      setError('Pick at least one format to generate.');
      return;
    }
    const bad = badGcsFields();
    if (bad.length > 0) {
      setError(`Fix these image URLs: ${bad.join('; ')}.`);
      return;
    }

    const sceneObj: ClientManifest['scenes'] = {
      hero: (scenes.hero ?? '').trim(),
      create: (scenes.create ?? '').trim(),
      conv: (scenes.conv ?? '').trim(),
      browse: (scenes.browse ?? '').trim(),
      winner: (scenes.winner ?? '').trim(),
    };
    if ((scenes.handover ?? '').trim()) sceneObj.handover = scenes.handover.trim();

    const business: ClientManifest['business'] = {
      image: bizImage.trim(),
      address: bizAddress.trim(),
    };
    if (bizRating.trim()) business.rating = bizRating.trim();
    if (bizReviews.trim()) business.reviews = bizReviews.trim();

    const manifest: ClientManifest = {
      slug: effectiveSlug,
      sponsor_name: sponsorName.trim(),
      industry,
      prize_label: prizeLabel.trim(),
      prize_short: prizeShort.trim(),
      prize_spoken: prizeSpoken.trim() || undefined,
      prize_value_label: prizeValueLabel.trim() || undefined,
      url: url.trim(),
      register_url: registerUrl.trim(),
      brand,
      scenes: sceneObj,
      page_image: pageImage.trim(),
      qr_image: qrImage.trim(),
      prize_image: prizeImage.trim() || undefined,
      business,
      formats: [...formats],
    };

    setBusy(true);
    try {
      const res = await generatePackFromManifest(manifest);
      setResult(res);
      onGenerated(); // refresh the library — the PDF is already there
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      const miss = manifestMissing(err);
      if (miss) {
        setMissing(miss);
      } else {
        setError(
          err instanceof ApiError ? err.message : 'Could not generate the pack from the manifest.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5" aria-label="Generate full pack from manifest">
      {/* Identity */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Identity</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="mf-sponsor"
            label="Sponsor name"
            value={sponsorName}
            onChange={setSponsorName}
            required
            placeholder="Bright Smile Dental"
          />
          <Field
            id="mf-slug"
            label="Slug"
            value={effectiveSlug}
            onChange={(v) => {
              setSlugTouched(true);
              setSlug(v);
            }}
            required
            hint="lowercase-kebab — GCS folder + filename segment."
          />
          <div>
            <label htmlFor="mf-industry" className="block text-sm font-medium text-ink-700">
              Industry
            </label>
            <select
              id="mf-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value as ManifestIndustry)}
              className="mt-1 min-h-[44px] w-full cursor-pointer rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {MANIFEST_INDUSTRIES.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>
          <Field
            id="mf-prize-label"
            label="Prize label"
            value={prizeLabel}
            onChange={setPrizeLabel}
            required
            placeholder="A free smile makeover"
          />
          <Field
            id="mf-prize-short"
            label="Prize (short)"
            value={prizeShort}
            onChange={setPrizeShort}
            required
            placeholder="Smile makeover"
          />
          <Field
            id="mf-prize-spoken"
            label="Prize (spoken)"
            value={prizeSpoken}
            onChange={setPrizeSpoken}
            placeholder="a free smile makeover"
          />
          <Field
            id="mf-prize-value"
            label="Prize value label"
            value={prizeValueLabel}
            onChange={setPrizeValueLabel}
            placeholder="R25,000"
          />
          <Field
            id="mf-url"
            label="Website URL"
            value={url}
            onChange={setUrl}
            required
            placeholder="https://brightsmile.co.za"
          />
          <Field
            id="mf-register-url"
            label="Register URL"
            value={registerUrl}
            onChange={setRegisterUrl}
            required
            placeholder="https://brightsmile.co.za/win"
          />
        </div>
      </fieldset>

      {/* Scenes */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Scene backgrounds</legend>
        <p className="text-xs text-ink-500">All must be https://storage.googleapis.com/… URLs.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {MANIFEST_SCENES.map((s) => (
            <Field
              key={s.key}
              id={`mf-scene-${s.key}`}
              label={`${s.label} URL`}
              value={scenes[s.key] ?? ''}
              onChange={(v) => setScene(s.key, v)}
              required={s.required}
              placeholder="https://storage.googleapis.com/…"
            />
          ))}
        </div>
      </fieldset>

      {/* Page / QR / prize images */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Page, QR &amp; prize images</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="mf-page-image"
            label="Page image URL"
            value={pageImage}
            onChange={setPageImage}
            required
            placeholder="https://storage.googleapis.com/…"
          />
          <Field
            id="mf-qr-image"
            label="QR image URL"
            value={qrImage}
            onChange={setQrImage}
            required
            placeholder="https://storage.googleapis.com/…"
          />
          <Field
            id="mf-prize-image"
            label="Prize image URL"
            value={prizeImage}
            onChange={setPrizeImage}
            placeholder="https://storage.googleapis.com/…"
          />
        </div>
      </fieldset>

      {/* Business */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Business</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="mf-biz-image"
            label="Business image URL"
            value={bizImage}
            onChange={setBizImage}
            required
            placeholder="https://storage.googleapis.com/…"
          />
          <Field
            id="mf-biz-address"
            label="Address"
            value={bizAddress}
            onChange={setBizAddress}
            required
            placeholder="12 Main Rd, Cape Town"
          />
          <Field
            id="mf-biz-rating"
            label="Rating"
            value={bizRating}
            onChange={setBizRating}
            placeholder="4.9"
          />
          <Field
            id="mf-biz-reviews"
            label="Reviews"
            value={bizReviews}
            onChange={setBizReviews}
            placeholder="312"
          />
        </div>
      </fieldset>

      {/* Brand colours */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Brand colours</legend>
        <p className="text-xs text-ink-500">
          Defaults to the dentist-cyan palette — adjust per tenant.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {BRAND_FIELDS.map((b) => (
            <div key={b.key}>
              <label
                htmlFor={`mf-brand-${b.key}`}
                className="block text-sm font-medium text-ink-700"
              >
                {b.label}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  value={brand[b.key]}
                  onChange={(e) => setBrandField(b.key, e.target.value)}
                  className="h-11 w-11 flex-shrink-0 cursor-pointer rounded-lg border border-line"
                  aria-label={`${b.label} colour picker`}
                />
                <input
                  id={`mf-brand-${b.key}`}
                  type="text"
                  value={brand[b.key]}
                  onChange={(e) => setBrandField(b.key, e.target.value)}
                  className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          ))}
        </div>
      </fieldset>

      {/* Formats */}
      <fieldset>
        <legend className="text-sm font-semibold text-ink">Formats to generate</legend>
        <div className="mt-2 space-y-2">
          {MANIFEST_FORMATS.map((fmt) => (
            <label key={fmt} className="flex min-h-[44px] cursor-pointer items-start gap-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={formats.has(fmt)}
                onChange={() => toggleFormat(fmt)}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded border-line text-primary focus:ring-primary"
                aria-label={MANIFEST_FORMAT_LABELS[fmt].label}
              />
              <span>
                <span className="font-medium text-ink-700">{MANIFEST_FORMAT_LABELS[fmt].label}</span>{' '}
                <span className="text-xs text-ink-500">— {MANIFEST_FORMAT_LABELS[fmt].hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {error && (
        <p role="alert" className="text-sm text-rose-600">
          {error}
        </p>
      )}
      {missing && <ManifestMissingPanel missing={missing} />}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Generating pack…' : 'Generate full pack from manifest'}
      </button>

      {result && <ManifestResultPanel result={result} />}
    </form>
  );
}

// --- page --------------------------------------------------------------------

/** Render-tier toggle (v5-3) — flip the default CPU/GPU video render tier.
 *  CPU is the cost floor; GPU is the fast opt-in. Persists server-side. */
function RenderTierToggle() {
  const [tier, setTier] = useState<'cpu' | 'gpu' | null>(null);
  const [gpuAvailable, setGpuAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getRenderTier()
      .then((s) => {
        setTier(s.default_tier);
        setGpuAvailable(s.gpu_available);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return;
        setError('Could not load render tier.');
      });
  }, []);

  async function choose(next: 'cpu' | 'gpu') {
    if (next === tier || busy) return;
    setBusy(true);
    setError(null);
    try {
      const s = await setRenderTier(next);
      setTier(s.default_tier);
      setGpuAvailable(s.gpu_available);
    } catch {
      setError('Could not update render tier.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface-alt px-4 py-3">
      <div className="flex flex-col">
        <span className="font-heading text-sm font-bold text-ink">Video render tier</span>
        <span className="text-xs text-ink-500">
          CPU is the cost floor (default). GPU is faster but billed while rendering — falls back to CPU if unavailable.
        </span>
      </div>
      <div className="ml-auto inline-flex rounded-lg border border-line p-1" role="group" aria-label="Render tier">
        {(['cpu', 'gpu'] as const).map((t) => {
          const active = tier === t;
          const disabled = busy || (t === 'gpu' && !gpuAvailable);
          return (
            <button
              key={t}
              type="button"
              onClick={() => choose(t)}
              disabled={disabled}
              aria-pressed={active}
              title={t === 'gpu' && !gpuAvailable ? 'GPU worker not configured' : undefined}
              className={`cursor-pointer rounded-md px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                active ? 'bg-primary text-white' : 'text-ink-700 hover:bg-white'
              } ${disabled && !active ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {t.toUpperCase()}
            </button>
          );
        })}
      </div>
      {error && <span className="w-full text-xs text-rose-600">{error}</span>}
    </div>
  );
}

export function AdminDealsPage() {
  const [assets, setAssets] = useState<CreativeAsset[] | null>(null);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [proposals, setProposals] = useState<ProposalListItem[] | null>(null);
  const [proposalsError, setProposalsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, CreativeAsset>>(new Map());
  const [justCreated, setJustCreated] = useState<string | null>(null);
  // v3-5 corrections & versioning loop state.
  const [historyLineage, setHistoryLineage] = useState<string | null>(null);
  const [historyReloadKey, setHistoryReloadKey] = useState(0);
  const [editAsset, setEditAsset] = useState<CreativeAsset | null>(null);

  function reloadProposals() {
    listProposals()
      .then(setProposals)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return;
        setProposalsError(err instanceof ApiError ? err.message : 'Could not load proposals.');
      });
  }

  function reloadAssets() {
    listCreativeAssets()
      .then(setAssets)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return;
        setAssetsError(err instanceof ApiError ? err.message : 'Could not load the library.');
      });
  }

  function handleDeleteAsset(asset: CreativeAsset) {
    const ok = window.confirm(
      `Delete "${asset.title}"?\n\nThis removes it from the library and deletes the file. ` +
        `Any proposal using it loses that one creative. This can't be undone.`,
    );
    if (!ok) return;
    deleteCreativeAsset(asset.id)
      .then(() => {
        // Drop it from the selected-for-attach set too, then refresh.
        setSelected((prev) => {
          const next = new Map(prev);
          next.delete(asset.id);
          return next;
        });
        reloadAssets();
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return;
        setAssetsError(err instanceof ApiError ? err.message : 'Could not delete the asset.');
      });
  }

  /**
   * Patch a single asset's poster_url in-place after a successful setVideoPoster
   * call — avoids a full library reload for a single field change.
   */
  function handlePosterChange(updated: CreativeAsset) {
    setAssets((cur) =>
      cur
        ? cur.map((a) => (a.id === updated.id ? { ...a, poster_url: updated.poster_url } : a))
        : cur,
    );
  }

  useEffect(() => {
    reloadAssets();
    reloadProposals();
  }, []);

  function toggleAsset(a: CreativeAsset) {
    setSelected((cur) => {
      const next = new Map(cur);
      if (next.has(a.id)) next.delete(a.id);
      else next.set(a.id, a);
      return next;
    });
  }

  const attached = Array.from(selected.values());

  async function changeStatus(id: string, status: ProposalStatus) {
    try {
      await updateProposal(id, { status });
      reloadProposals();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setProposalsError(err instanceof ApiError ? err.message : 'Could not update status.');
    }
  }

  // Keep the visible item_count in sync when an item is added/removed on an
  // expanded proposal (avoids a full list refetch for a single-item change).
  function updateItemCount(id: string, count: number) {
    setProposals((cur) =>
      cur ? cur.map((p) => (p.id === id ? { ...p, item_count: count } : p)) : cur,
    );
  }

  async function removeProposal(id: string) {
    if (!window.confirm('Delete this proposal? The share link will stop working.')) return;
    try {
      await deleteProposal(id);
      reloadProposals();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setProposalsError(err instanceof ApiError ? err.message : 'Could not delete the proposal.');
    }
  }

  return (
    <AdminShell title="Deals & Proposals">
      <RenderTierToggle />
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Panel
          title="Creative Library"
          subtitle="Every client's videos & decks. Play them here, tick “Attach” to add to a proposal."
        >
          {assets === null && !assetsError && <p className="text-sm text-ink-500">Loading…</p>}
          {assetsError && <p className="text-sm text-rose-600">{assetsError}</p>}
          {assets && (
            <CreativeLibrary
              assets={assets}
              selected={selected}
              onToggle={toggleAsset}
              onEditRegenerate={(a) => setEditAsset(a)}
              onShowHistory={(lineageId) => setHistoryLineage(lineageId)}
              onDelete={handleDeleteAsset}
              onPosterChange={handlePosterChange}
            />
          )}
        </Panel>

        <div className="space-y-6">
          <section className="rounded-2xl border border-line bg-white shadow-card">
            <details className="group">
              <summary className="flex min-h-[44px] cursor-pointer list-none items-start justify-between gap-3 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-6">
                <span>
                  <span className="font-heading text-base font-bold text-ink">
                    Generate full pack (from manifest)
                  </span>
                  <span className="mt-0.5 block text-sm text-ink-500">
                    One manifest → a client's whole pack (videos + PDF + proposal). All image
                    fields must be storage.googleapis.com URLs.
                  </span>
                </span>
                <svg
                  viewBox="0 0 24 24"
                  className="mt-1 h-5 w-5 flex-shrink-0 text-ink-500 transition-transform duration-200 group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>
              <div className="border-t border-line p-5 sm:p-6">
                <GeneratePackFromManifestForm onGenerated={reloadAssets} />
              </div>
            </details>
          </section>

          <Panel
            title="Generate full pack"
            subtitle="One form → a client's whole pack (image, PDF, voiceover, videos) + an optional draft proposal. Videos render in the background; everything lands in the Creative Library."
          >
            <GenerateFullPackForm onGenerated={reloadAssets} />
          </Panel>

          <Panel
            title="Generate Image"
            subtitle="Generate a photoreal hero image — it lands in the Creative Library and can be reused as the hero URL for the PDF & video."
          >
            <GenerateImageForm onGenerated={reloadAssets} />
          </Panel>

          <Panel
            title="Generate Pitch PDF"
            subtitle="Render a branded pitch deck from the website — it lands in the Creative Library."
          >
            <GeneratePitchPdfForm onGenerated={reloadAssets} />
          </Panel>

          <Panel
            title="Generate Video"
            subtitle="Render a pitch or TV-ad video — it renders in the background and lands in the Creative Library."
          >
            <GenerateVideoForm onGenerated={reloadAssets} />
          </Panel>

          <Panel
            title="Generate Voiceover"
            subtitle="Generate a voiceover from a script — it lands in the Creative Library with an inline audio player."
          >
            <GenerateVoiceoverForm onGenerated={reloadAssets} />
          </Panel>

          <Panel
            title="New proposal"
            subtitle="Generates a private, unguessable share link — send it to your client."
          >
            {justCreated && (
              <div className="mb-4 rounded-xl border border-primary-200 bg-primary-50 p-3">
                <p className="text-sm font-semibold text-primary-700">Proposal created.</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a
                    href={proposalShareUrl(justCreated)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 max-w-full truncate text-xs text-primary-700 underline"
                    title={proposalShareUrl(justCreated)}
                  >
                    {proposalShareUrl(justCreated)}
                  </a>
                  <CopyLinkButton url={proposalShareUrl(justCreated)} />
                </div>
              </div>
            )}
            <NewProposalForm
              attachedFromLibrary={attached}
              onRemoveLibraryItem={(id) =>
                setSelected((cur) => {
                  const next = new Map(cur);
                  next.delete(id);
                  return next;
                })
              }
              onCreated={(token) => {
                setSelected(new Map());
                setJustCreated(token);
                reloadProposals();
              }}
            />
          </Panel>

          <Panel title="Proposals">
            {proposals === null && !proposalsError && <p className="text-sm text-ink-500">Loading…</p>}
            {proposalsError && <p className="text-sm text-rose-600">{proposalsError}</p>}
            {proposals && (
              <ProposalsList
                proposals={proposals}
                assets={assets ?? []}
                onStatusChange={changeStatus}
                onDelete={removeProposal}
                onItemCountChange={updateItemCount}
              />
            )}
          </Panel>
        </div>
      </div>

      {/* v3-5 — version history drawer. */}
      {historyLineage && (
        <VersionHistoryDrawer
          lineageId={historyLineage}
          reloadKey={historyReloadKey}
          onClose={() => setHistoryLineage(null)}
          onChanged={() => {
            reloadAssets();
            setHistoryReloadKey((k) => k + 1);
          }}
        />
      )}

      {/* v3-5 — edit & regenerate modal (reopens the matching generator prefilled). */}
      {editAsset && (
        <EditRegenerateModal
          asset={editAsset}
          onClose={() => setEditAsset(null)}
          onGenerated={() => {
            reloadAssets();
            setHistoryReloadKey((k) => k + 1);
          }}
        />
      )}
    </AdminShell>
  );
}

export default AdminDealsPage;
